import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CheckCircle, XCircle, AlertCircle, DollarSign, Clock, TrendingUp, Activity, Users, Target, Zap, BarChart3, PieChart as PieChartIcon, FileText, AlertTriangle, ArrowDownRight, Bell, MoreVertical, Trash2, RefreshCw, Search, Settings, Download } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter } from 'recharts';
import { useNotificationStore } from '../stores/notificationStore';
import DecisionCard from './DecisionCard';

export interface SuspiciousRecipient {
  name: string;
  total_sent: number;
  total_received: number;
  transaction_count: number;
  pct_round: number;
  classification: 'confirmed_lender' | 'possible_informal' | 'normal';
}

export interface PaydayEvent {
  date: string;
  income: number;
  same_day_out: number;
  drain_pct: number;
}

export interface IBVDecision {
  id: string;
  reference_id: string;
  customer_name: string;
  decision: 'APPROVED' | 'DECLINE' | 'DECLINED' | 'REVIEW';
  approved: boolean;
  high_risk?: boolean;
  confidence_score?: number;
  criteria?: {
    hasJob: boolean;
    lowNSFRate: boolean;
    paysLendersBack: boolean;
    reasonableFees: boolean;
    loanAffordable: boolean;
    notOverleveraged: boolean;
    noHiddenLenders?: boolean;
    healthyPaydayPattern?: boolean;
  };
  etransfer_analysis?: {
    total_etransfers: number;
    etransfers_out: number;
    etransfers_in: number;
    unique_recipients: number;
    hidden_lender_count: number;
    hidden_lender_total: number;
    suspicious_recipients: SuspiciousRecipient[];
  };
  payday_drain_analysis?: {
    paydays_analyzed: number;
    avg_drain_pct: number;
    over_100pct_count: number;
    debt_spiral_score: number;
    pattern: string;
    worst_events: PaydayEvent[];
  };
  raw_ibv_data?: any;
  data_extraction?: {  // NEW: Stage 1 output from Rust processor
    accounts: Array<{
      bank: string;
      institution?: string;
      account_number_last4?: string;
      account_type?: string;
      transaction_count: number;
      transactions: Array<{
        date: string;
        description: string;
        category?: string;
        amount: number;
        tx_type: string;
        balance: number;
        debit?: number;
        credit?: number;
        tags: string[];
        flags: string[];
        metadata: {
          is_income: boolean;
          is_gambling: boolean;
          is_nsf: boolean;
          is_lender_payment: boolean;
          is_fee: boolean;
          is_etransfer: boolean;
          is_atm: boolean;
          likely_payday: boolean;
        };
      }>;
    }>;
    extraction_summary: {
      total_accounts: number;
      total_transactions: number;
      date_range: {
        earliest: string;
        latest: string;
        days_covered: number;
      };
      transaction_types: {
        credits: number;
        debits: number;
      };
      categories_found: string[];
      tags_applied: Record<string, number>;
    };
  };
  reasons: string[] | null;
  decision_reasoning?: string;
  context?: {
    currentBalance: number;
    avgIncome: number;
    balanceAfterPayroll: number;
    returns30: number;
    nsf30: number;
  };
  timestamp: string;
  created_at?: string;
  api_cost?: number;
  revenue?: number;
  profit?: number;
  nsf_count?: number;
  nsf_per_month?: number;
  total_fees?: number;
  nsf_fee_total?: number;
  overdraft_fee_total?: number;
  loan_bounce_rate?: number;
  lender_count?: number;
  loan_load_pct?: number;
  payday_loan_pct?: number;
  monthly_income_avg?: number;
  loan_requested?: number;
  llm_recommendation?: string;
  ai_decision?: string; // AI's own decision: APPROVE, DECLINE, REVIEW
  llm_processing_time_ms?: number;
  rust_processing_time_ms?: number;
  account_age_days?: number;
  current_overdraft_status?: boolean;
  balance_trend?: string;
  nsf_trend?: string;
  cash_flow_ratio?: number;
  cash_flow_status?: string;
  loan_net_30d?: number;
  loan_net_90d?: number;
  loan_net_365d?: number;
  loan_position_trend?: string;
  trend_analysis?: {
    income_trend: string;
    nsf_trend: string;
    lender_trend: string;
    spending_trend: string;
    balance_trend: string;
    loan_position_trend: string;
  };
  debug_info?: any;
  llm_reasoning?: string;
  llm_pending?: boolean;
  llm_job_id?: string;
  rust_metrics?: {
    metrics: Array<{
      name: string;
      value: number;
      threshold: number;
      passes: boolean;
      severity: 'Low' | 'Moderate' | 'High' | 'Critical';
    }>;
    summary: {
      total_checks: number;
      passed: number;
      failed: number;
      critical_count: number;
      monthly_income: number;
    };
  };
  // Enhanced IBV Processor Fields
  income_analysis?: {
    monthly_income: number;
    payroll_income: number;
    government_income: number;
    other_income: number;
    income_stability: number;
    median_income: number;
    high_variance: boolean;
    outliers_excluded_total: number;
    outliers_excluded_count: number;
  };
  fee_analysis?: {
    nsf_count: number;
    nsf_fees: number;
    overdraft_count: number;
    overdraft_fees: number;
    total_fees: number;
  };
  lender_analysis?: {
    lenders: Array<{
      name: string;
      bounce_count: number;
      total_borrowed: number;
      grade: string;
    }>;
    total_payday_debt: number;
    highest_risk_lender?: string;
  };
  spending_analysis?: {
    risky_spending: number;
    essential_spending: number;
    atm_withdrawal_count: number;
    credit_card_payments: number;
    discretionary_spending: number;
    alcohol_to_groceries_ratio: number;
  };
  ai_summary?: string; // Quick 2-3 sentence summary for main card
  ai_full_analysis?: string; // Full detailed analysis for report
  audit_log_path?: string;
  forensic_analysis?: {
    gambling: {
      transactions: Array<{ date: string; merchant: string; amount: string; tx_type: string }>;
      total_wagered: number;
      total_won: number;
      net_loss: number;
      merchant_count: number;
      frequency: string;
      unique_merchants: string[];
    };
    cash_patterns: {
      repetitive_amounts: Array<{ amount: number; count: number; avg_days_between: number; risk_level: string }>;
      total_atm_count: number;
      total_atm_amount: number;
      pattern_detected: boolean;
      atm_transactions?: Array<{
        line_number: number;
        date: string;
        amount: number;
      }>;
    };
    deposit_seizure: {
      patterns: Array<{
        deposit_line: number;
        deposit_date: string;
        deposit_amount: number;
        seizure_line: number;
        seizure_date: string;
        seizure_amount: number;
        days_between: number;
        likely_creditor: string;
      }>;
      total_seized: number;
      pattern_count: number;
      burned_account: boolean;
    };
    etransfer_activity: {
      outbound_count: number;
      outbound_total: number;
      inbound_count: number;
      inbound_total: number;
      suspicious_recipients: string[];
      rapid_fire_detected: boolean;
    };
    nsf_summary: {
      nsf_count_30d: number;
      nsf_count_90d: number;
      nsf_fees_30d: number;
      nsf_fees_90d: number;
      overdraft_count_30d: number;
      overdraft_count_90d: number;
      assessment: string;
    };
    lender_payments?: {
      transactions: Array<{
        line_number: number;
        date: string;
        lender: string;
        amount: number;
      }>;
      total_paid: number;
      lender_count: number;
      unique_lenders: string[];
      cost_per_paycheque: number;
      hiding_money_detected: boolean;
      overleveraged: boolean;
    };
  };
  risk_assessment?: {
    risk_score: number;
    nsf_probability: number;
    recommendation: string;
    borrower_strategy?: string;
    will_they_repay?: string;
    debt_spiral_depth?: string;
    red_flags?: string[];
    summary?: string;
  };
  senior_assessment?: {
    risk_level: string;
    confidence: number;
    senior_decision: string;
    full_text: string;  // Detailed analysis (paywall)
    narrative_summary: string;  // Brief summary (free)
    key_concerns?: string[];
    approval_conditions?: string[];
    model_used?: string;
    processing_time_ms?: number;
  };
  // NEW: Stage 2.5 Uncertainty Resolution
  uncertainty_resolution?: {
    resolutions: Array<{
      index: number;
      answer: string;  // "loan_disbursement" | "income" | "fee" | "not_fee" | "manual_review"
      reasoning: string;
      confidence: number;  // 0-100 (LLM confidence)
      original_uncertainty: {
        analyzer: string;
        category: string;
        transaction_index?: number;
        transaction_description?: string;
        transaction_amount?: number;
        question: string;
        context: any;
        confidence: number;  // 0.0-1.0 (Rust confidence)
        severity: 'Low' | 'Medium' | 'High';
      };
    }>;
    processing_time_ms: number;
    model_used: string;
    skipped: boolean;
    total_resolved: number;
    error?: string;
  };
  rust_result?: {
    uncertainty_report: {
      items: Array<any>;
      total_count: number;
      high_severity_count: number;
      requires_llm: boolean;
    };
  };
  // NEW: Stage 2.7 Transaction Validation
  transaction_validation?: {
    validations: Array<{
      index: number;
      validated_category: 'NSF' | 'Overdraft' | 'Account_Fee' | 'Stop_Payment' | 'Other';
      confidence: number;  // 0-100
      correction_needed: boolean;
      reasoning: string;
    }>;
    corrections_count: number;
    processing_time_ms: number;
    model_used: string;
    skipped: boolean;
  };
  // Webhook response fields
  risk_level?: string;
  nsf_count_30d?: number;
  overdraft_count_30d?: number;
  processing_time_ms?: number;
  stage_timings?: {
    rust_processing_ms?: number;
    junior_qa_ms?: number;
    uncertainty_resolution_ms?: number;
    senior_assessment_ms?: number;
  };
  uncertainty_report?: any;
}

interface ModelInfo {
  name: string;
  size: string;
  speed: string;
  quality: string;
  vram: string;
}

interface ModelsResponse {
  success: boolean;
  current_model: string;
  current_model_info: ModelInfo;
  available_models: Record<string, ModelInfo>;
}

// ============================================================================
// UNCERTAINTY RESOLUTION UI COMPONENTS
// ============================================================================

interface UncertaintyItem {
  analyzer: string;
  category: string;
  transaction_index?: number;
  transaction_description?: string;
  transaction_amount?: number;
  question: string;
  context: any;
  confidence: number;
  severity: 'Low' | 'Medium' | 'High';
}

interface UncertaintyResolution {
  index: number;
  answer: string;
  reasoning: string;
  confidence: number;
  original_uncertainty: UncertaintyItem;
}

interface UncertaintyReport {
  items: UncertaintyItem[];
  total_count: number;
  high_severity_count: number;
  requires_llm: boolean;
}

interface UncertaintyResolutionData {
  resolutions: UncertaintyResolution[];
  processing_time_ms: number;
  model_used: string;
  skipped: boolean;
  total_resolved: number;
  pass_count?: number;  // Number of iterative passes
  convergence_achieved?: boolean;  // True if all uncertainties resolved
  error?: string;
}

interface UncertaintyBadgeProps {
  uncertaintyReport?: UncertaintyReport;
  uncertaintyResolution?: UncertaintyResolutionData;
}

const UncertaintyBadge: React.FC<UncertaintyBadgeProps> = ({
  uncertaintyReport,
  uncertaintyResolution
}) => {
  // Show if there were resolutions OR current uncertainties
  const hasResolutions = uncertaintyResolution && !uncertaintyResolution.skipped && uncertaintyResolution.total_resolved > 0;
  const hasCurrentUncertainties = uncertaintyReport && uncertaintyReport.total_count > 0;

  if (!hasResolutions && !hasCurrentUncertainties) return null;

  const totalCount = uncertaintyReport?.total_count || 0;
  const highCount = uncertaintyReport?.high_severity_count || 0;
  const resolvedCount = uncertaintyResolution?.total_resolved || 0;
  const passCount = uncertaintyResolution?.pass_count || 0;
  const converged = uncertaintyResolution?.convergence_achieved || false;

  // Show resolved state if converged, otherwise show current uncertainties
  const showResolved = converged && resolvedCount > 0;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
      showResolved
        ? 'bg-green-500/20 border-green-500/30 text-green-400'
        : 'bg-amber-500/20 border-amber-500/30 text-amber-400'
    }`}>
      <span className="text-base">{showResolved ? '✓' : '⚠'}</span>
      {showResolved ? (
        <>
          <span>{resolvedCount} resolved</span>
          {passCount > 1 && (
            <>
              <span className="text-green-600">•</span>
              <span className="text-green-300">{passCount} passes</span>
            </>
          )}
        </>
      ) : (
        <>
          <span>{totalCount} uncertain</span>
          {highCount > 0 && (
            <>
              <span className="text-amber-600">•</span>
              <span className="text-red-400">{highCount} high</span>
            </>
          )}
        </>
      )}
    </div>
  );
};

interface ResolutionTimelineProps {
  processingTime?: {
    rust_ms?: number;
    uncertainty_resolution_ms?: number;
    total_ms?: number;
  };
}

const ResolutionTimeline: React.FC<ResolutionTimelineProps> = ({ processingTime }) => {
  const stages = [
    {
      label: 'Rust Analyzers',
      icon: '⚙️',
      time: processingTime?.rust_ms,
      color: 'blue',
      description: 'Fee, Income, Lender'
    },
    {
      label: 'Uncertainties',
      icon: '⚠',
      time: null,
      color: 'amber',
      description: 'Flagged ambiguous'
    },
    {
      label: 'LLM Resolution',
      icon: '🤖',
      time: processingTime?.uncertainty_resolution_ms,
      color: 'green',
      description: 'deepseek-r1:8b'
    },
    {
      label: 'Final Decision',
      icon: '✓',
      time: null,
      color: 'purple',
      description: 'APPROVE/DECLINE'
    }
  ];

  return (
    <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-4 mb-4">
      <h6 className="text-xs font-bold text-gray-400 uppercase mb-3">Processing Timeline</h6>
      <div className="flex items-center justify-between gap-2">
        {stages.map((stage, idx) => (
          <React.Fragment key={idx}>
            <div className="flex flex-col items-center gap-1 flex-1">
              <div className={`w-10 h-10 bg-${stage.color}-500/20 border border-${stage.color}-500/30 rounded-full flex items-center justify-center`}>
                <span className="text-lg">{stage.icon}</span>
              </div>
              <span className="text-xs font-bold text-white text-center">{stage.label}</span>
              <span className="text-xs text-gray-500 text-center">{stage.description}</span>
              {stage.time && (
                <span className="text-xs text-gray-400 font-mono">{stage.time}ms</span>
              )}
            </div>
            {idx < stages.length - 1 && (
              <div className="flex-shrink-0 w-8 h-0.5 bg-gradient-to-r from-gray-600 to-gray-700 mb-12" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

interface UncertaintyPanelProps {
  uncertaintyReport?: UncertaintyReport;
  uncertaintyResolution?: UncertaintyResolutionData;
  processingTime?: {
    rust_ms?: number;
    uncertainty_resolution_ms?: number;
  };
}

const UncertaintyPanel: React.FC<UncertaintyPanelProps> = ({
  uncertaintyReport,
  uncertaintyResolution,
  processingTime
}) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandedAnalyzers, setExpandedAnalyzers] = useState<Set<string>>(new Set());
  const [confidenceFilter, setConfidenceFilter] = useState<'all' | 'high' | 'needs_review'>('all');

  // Show if there are resolutions OR current uncertainties
  const hasResolutions = uncertaintyResolution && !uncertaintyResolution.skipped && uncertaintyResolution.resolutions?.length > 0;
  const hasItems = uncertaintyReport && uncertaintyReport.items.length > 0;

  if (!hasResolutions && !hasItems) return null;

  const resolvedCount = uncertaintyResolution?.total_resolved || 0;
  const passCount = uncertaintyResolution?.pass_count || 0;
  const converged = uncertaintyResolution?.convergence_achieved || false;

  const toggleItem = (idx: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedItems(newExpanded);
  };

  const toggleAnalyzer = (analyzer: string) => {
    const newExpanded = new Set(expandedAnalyzers);
    if (newExpanded.has(analyzer)) {
      newExpanded.delete(analyzer);
    } else {
      newExpanded.add(analyzer);
    }
    setExpandedAnalyzers(newExpanded);
  };

  const resolutions = uncertaintyResolution?.resolutions || [];

  // Group resolutions by analyzer
  const groupedByAnalyzer = resolutions.reduce((acc, resolution, idx) => {
    const analyzer = resolution.original_uncertainty?.analyzer || 'unknown';
    if (!acc[analyzer]) acc[analyzer] = [];
    acc[analyzer].push({ resolution, originalIndex: idx });
    return acc;
  }, {} as Record<string, Array<{ resolution: any; originalIndex: number }>>);

  // Filter resolutions by confidence
  const filterByConfidence = (res: any) => {
    if (confidenceFilter === 'all') return true;
    if (confidenceFilter === 'high') return res.confidence >= 85;
    if (confidenceFilter === 'needs_review') return res.confidence < 70;
    return true;
  };

  // Apply filter to grouped data
  const filteredGroups = Object.entries(groupedByAnalyzer).reduce((acc, [analyzer, items]) => {
    const filtered = items.filter(item => filterByConfidence(item.resolution));
    if (filtered.length > 0) acc[analyzer] = filtered;
    return acc;
  }, {} as Record<string, Array<{ resolution: any; originalIndex: number }>>);

  return (
    <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg overflow-hidden mb-3">
      <div
        className="px-3 py-2 cursor-pointer hover:bg-gray-700/30 transition-all flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{converged ? '✓' : '⚠'}</span>
          <span className="text-xs font-semibold text-white">
            Uncertainties
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            converged
              ? 'bg-green-500/20 text-green-400'
              : 'bg-amber-500/20 text-amber-400'
          }`}>
            {converged ? `${resolvedCount} resolved` : `${uncertaintyReport?.total_count || 0} flagged`}
          </span>
          {passCount > 1 && (
            <span className="text-xs text-gray-400">• {passCount} passes</span>
          )}
        </div>
        <span className={`text-gray-400 text-xs transform transition-transform ${expanded ? 'rotate-180' : ''}`}>▼</span>
      </div>

      {expanded && (
        <div className="border-t border-gray-700/50 bg-gray-900/20">
          {/* Filter Dropdown */}
          <div className="px-3 py-2 border-b border-gray-700/50 flex items-center gap-2">
            <span className="text-xs text-gray-400">Filter:</span>
            <select
              value={confidenceFilter}
              onChange={(e) => setConfidenceFilter(e.target.value as any)}
              className="text-xs bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="all">All ({resolutions.length})</option>
              <option value="high">High Confidence ≥85% ({resolutions.filter(r => r.confidence >= 85).length})</option>
              <option value="needs_review">Needs Review &lt;70% ({resolutions.filter(r => r.confidence < 70).length})</option>
            </select>
          </div>

          {/* Grouped by Analyzer */}
          <div className="px-3 py-2 space-y-2">
            {Object.keys(filteredGroups).length > 0 ? (
              Object.entries(filteredGroups).map(([analyzer, items]) => {
                const isAnalyzerExpanded = expandedAnalyzers.has(analyzer);
                const analyzerName = analyzer.replace(/_/g, ' ');
                const avgConfidence = Math.round(items.reduce((sum, item) => sum + item.resolution.confidence, 0) / items.length);

                return (
                  <div key={analyzer} className="border border-gray-700/50 rounded bg-gray-800/30">
                    {/* Analyzer Group Header */}
                    <div
                      className="px-2 py-1.5 cursor-pointer hover:bg-gray-700/20 transition-all flex items-center justify-between"
                      onClick={() => toggleAnalyzer(analyzer)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-blue-400">{analyzerName}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-400">{items.length} items</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className={`text-xs ${avgConfidence >= 85 ? 'text-green-400' : avgConfidence >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
                          avg {avgConfidence}%
                        </span>
                      </div>
                      <span className={`text-gray-400 text-xs transform transition-transform ${isAnalyzerExpanded ? 'rotate-180' : ''}`}>▼</span>
                    </div>

                    {/* Analyzer Items */}
                    {isAnalyzerExpanded && (
                      <div className="border-t border-gray-700/50 space-y-1 p-2">
                        {items.map(({ resolution, originalIndex }) => {
                          const uncertainty = resolution.original_uncertainty;
                          const isExpanded = expandedItems.has(originalIndex);
                          const severityColor =
                            uncertainty.severity === 'High' ? 'text-red-400' :
                            uncertainty.severity === 'Medium' ? 'text-yellow-400' :
                            'text-blue-400';

                          return (
                            <div key={originalIndex} className="border border-gray-700/50 rounded bg-gray-800/50">
                              <div
                                className="px-2 py-1.5 cursor-pointer hover:bg-gray-700/30 transition-all"
                                onClick={(e) => { e.stopPropagation(); toggleItem(originalIndex); }}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className={`text-xs font-mono ${severityColor}`}>
                                      {uncertainty.severity === 'High' ? 'H' : uncertainty.severity === 'Medium' ? 'M' : 'L'}
                                    </span>
                                    <span className="text-xs text-white truncate flex-1">
                                      {uncertainty.transaction_description || uncertainty.question.substring(0, 60)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {resolution.answer && (
                                      <span className="text-xs text-green-400 font-medium">
                                        {resolution.answer.replace(/_/g, ' ').substring(0, 20)}
                                      </span>
                                    )}
                                    <span className={`text-xs ${
                                      resolution.confidence >= 85 ? 'text-green-400' :
                                      resolution.confidence >= 70 ? 'text-yellow-400' :
                                      'text-red-400'
                                    }`}>
                                      {resolution.confidence}%
                                    </span>
                                    <span className={`text-gray-400 text-xs transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                                  </div>
                                </div>
                              </div>

                              {isExpanded && (
                                <div className="px-3 py-2 border-t border-gray-700/50 bg-gray-900/30 space-y-2">
                                  <div>
                                    <div className="text-xs text-gray-400 mb-1">Question</div>
                                    <div className="text-xs text-white">{uncertainty.question}</div>
                                  </div>

                                  {uncertainty.transaction_description && (
                                    <div>
                                      <div className="text-xs text-gray-400 mb-1">Transaction</div>
                                      <div className="flex justify-between items-start bg-gray-800/50 rounded px-2 py-1">
                                        <span className="text-xs text-gray-300">{uncertainty.transaction_description}</span>
                                        {uncertainty.transaction_amount !== undefined && (
                                          <span className="text-xs text-white font-mono ml-2">
                                            ${uncertainty.transaction_amount.toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {resolution.answer && (
                                    <div>
                                      <div className="text-xs text-gray-400 mb-1">LLM Resolution</div>
                                      <div className="bg-green-500/10 border border-green-500/20 rounded px-2 py-1.5">
                                        <div className="text-xs font-semibold text-green-400 mb-0.5">
                                          {resolution.answer.replace(/_/g, ' ')}
                                        </div>
                                        <div className="text-xs text-gray-300">{resolution.reasoning}</div>
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex gap-4 text-xs">
                                    <div>
                                      <span className="text-gray-400">Rust confidence:</span>
                                      <span className="text-white ml-1">{(uncertainty.confidence * 100).toFixed(0)}%</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Category:</span>
                                      <span className="text-white ml-1">{uncertainty.category}</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-4 text-xs text-gray-500">
                No uncertainties match filter
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function IBVApprovals() {
  const [decisions, setDecisions] = useState<IBVDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dbStats, setDbStats] = useState({ total: 0, approved: 0, declined: 0, review: 0, approvalRate: 0 });
  const [currentModel, setCurrentModel] = useState<string>('');
  const [availableModels, setAvailableModels] = useState<Record<string, ModelInfo>>({});
  const [modelSwitching, setModelSwitching] = useState(false);
  const [verboseData, setVerboseData] = useState<any>(null);
  const [verboseLoading, setVerboseLoading] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedAiReasoning, setSelectedAiReasoning] = useState<{ reasoning: string; decision: IBVDecision } | null>(null);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState<'decisions' | 'approved' | 'declined' | 'review' | 'rate' | 'llm-agreement' | null>(null);
  const [clientAnalyticsOpen, setClientAnalyticsOpen] = useState(false);
  const [selectedClientDecision, setSelectedClientDecision] = useState<IBVDecision | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedReviewDecision, setSelectedReviewDecision] = useState<IBVDecision | null>(null);
  const [reviewUpdating, setReviewUpdating] = useState(false);

  // Background deep analysis state
  const [deepAnalysisStatus, setDeepAnalysisStatus] = useState<Map<string, 'running' | 'complete'>>(new Map());
  const [deepAnalysisResults, setDeepAnalysisResults] = useState<Map<string, string>>(new Map());
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [deepAnalysisRunning, setDeepAnalysisRunning] = useState(false);
  const [deepAnalysisResult, setDeepAnalysisResult] = useState<string | null>(null);
  const [deepAnalysisTimer, setDeepAnalysisTimer] = useState(0);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReportDecision, setSelectedReportDecision] = useState<IBVDecision | null>(null);
  const [expandedAnalyzers, setExpandedAnalyzers] = useState<Record<string, string | null>>({});
  const [rawDataModalOpen, setRawDataModalOpen] = useState(false);
  const [rawIbvModalOpen, setRawIbvModalOpen] = useState(false);
  const [unifiedRawDataModalOpen, setUnifiedRawDataModalOpen] = useState(false);
  const [selectedUnifiedRawDecision, setSelectedUnifiedRawDecision] = useState<IBVDecision | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [decisionFilter, setDecisionFilter] = useState<'all' | 'review' | 'approved' | 'declined'>('all');

  // Queue statistics state
  const [queueStats, setQueueStats] = useState<{ active: number; queued: number; limit: number } | null>(null);

  // Track newly completed decisions for fade-in effect
  const [completedDecisions, setCompletedDecisions] = useState<Set<string>>(new Set());

  // Processing overlay state (real-time pipeline progress)
  const [processingStatus, setProcessingStatus] = useState<Map<string, {
    customer_name?: string;
    queued?: boolean;
    queue_position?: number;
    queue_depth?: number;
    active_processing?: number;
    stages: {
      extraction: 'pending' | 'started' | 'completed';
      analysis: 'pending' | 'started' | 'completed';
      qa: 'pending' | 'started' | 'completed';
      assessment: 'pending' | 'started' | 'completed';
      saving: 'pending' | 'started' | 'completed';
    };
    decision?: string;
    complete: boolean;
  }>>(new Map());

  // Categorization state
  const [categorizationModalOpen, setCategorizationModalOpen] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<string>('');
  const [customRules, setCustomRules] = useState<Array<{id: number, pattern: string, badge_type: string, badge_emoji: string, badge_color: string}>>([]);

  // Smart token formatter: 1, 2, 999, 1.0k, 10.5k, 100.3k, 1.0M
  const formatTokens = (tokens: number): string => {
    if (tokens < 1000) {
      return Math.floor(tokens).toString();
    } else if (tokens < 1000000) {
      return (tokens / 1000).toFixed(1) + 'k';
    } else {
      return (tokens / 1000000).toFixed(1) + 'M';
    }
  };

  // Money formatter: 1, 10, 100, 1,000, 10,000, 100,000, 1,000,000 (comma-separated)
  const formatMoney = (amount: number | null | undefined | string, decimals: number = 0): string => {
    if (amount === null || amount === undefined || amount === '') return '0';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '0';
    return numAmount.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Badge definitions for categorization
  const BADGE_OPTIONS = [
    { type: 'FOOD', emoji: '🍔', color: 'bg-orange-700/30 border-orange-600/70 text-orange-300', label: 'Food' },
    { type: 'GROCERIES', emoji: '🛒', color: 'bg-green-700/30 border-green-600/70 text-green-300', label: 'Groceries' },
    { type: 'SHOPPING', emoji: '🛍️', color: 'bg-pink-700/30 border-pink-600/70 text-pink-300', label: 'Shopping' },
    { type: 'RIDESHARE', emoji: '🚗', color: 'bg-blue-700/30 border-blue-600/70 text-blue-300', label: 'Rideshare' },
    { type: 'LOAN_IN', emoji: '💰', color: 'bg-green-700/30 border-green-600/70 text-green-300', label: 'Loan In' },
    { type: 'LOAN_OUT', emoji: '💸', color: 'bg-red-700/30 border-red-600/70 text-red-300', label: 'Loan Out' },
    { type: 'PAYCHECK', emoji: '💰', color: 'bg-green-700/30 border-green-600/70 text-green-300', label: 'Paycheck' },
    { type: 'NSF_REPAYMENT', emoji: '💳', color: 'bg-yellow-700/30 border-yellow-600/70 text-yellow-300', label: 'NSF Repayment' },
    { type: 'HEALTHCARE', emoji: '🏥', color: 'bg-red-700/30 border-red-600/70 text-red-300', label: 'Healthcare' },
    { type: 'FITNESS', emoji: '💪', color: 'bg-orange-700/30 border-orange-600/70 text-orange-300', label: 'Fitness' },
    { type: 'ATM', emoji: '💰', color: 'bg-blue-700/30 border-blue-600/70 text-blue-300', label: 'ATM' },
    { type: 'GAMBLING', emoji: '🎰', color: 'bg-red-500/20 border-red-500/50 text-red-400', label: 'Gambling' },
  ];

  // Categorization: Click description to teach the system
  const handleCategorizeTxn = (description: string) => {
    console.log('CLICKED:', description);
    console.log('Opening modal...');
    setSelectedPattern(description);
    setCategorizationModalOpen(true);
    console.log('Modal state set to true');
  };

  const handleSaveCategorization = async (badge: {type: string, emoji: string, color: string, label: string}) => {
    const pattern = selectedPattern.toLowerCase().trim();

    try {
      // Save to API (Supabase + RAG)
      const response = await fetch('/api/ibv/categorization-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern,
          badge_type: badge.type,
          badge_emoji: badge.emoji,
          badge_color: badge.color,
          created_by: 'ui-manual'
        })
      });

      const data = await response.json();
      if (data.success) {
        // Refresh rules to apply immediately
        const rulesResponse = await fetch('/api/ibv/categorization-rules');
        const rulesData = await rulesResponse.json();
        if (rulesData.success) {
          setCustomRules(rulesData.rules);
        }
        console.log(`✅ Saved: "${pattern}" → ${badge.emoji} ${badge.type} (applies globally + saved to RAG)`);
      }
    } catch (error) {
      console.error('Save failed:', error);
    }

    setCategorizationModalOpen(false);
  };

  const [selectedRawIbvDecision, setSelectedRawIbvDecision] = useState<IBVDecision | null>(null);
  const [selectedRawDecision, setSelectedRawDecision] = useState<IBVDecision | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);

  // Spec downloads state
  const [specs, setSpecs] = useState<Array<{
    filename: string;
    name: string;
    size: number;
    modified: string;
    download_url: string;
  }>>([]);
  const [specsLoading, setSpecsLoading] = useState(true);
  const [selectedTransactionDecision, setSelectedTransactionDecision] = useState<IBVDecision | null>(null);
  const [txnSortField, setTxnSortField] = useState<'date' | 'amount' | 'balance'>('date');
  const [txnSortDirection, setTxnSortDirection] = useState<'asc' | 'desc'>('desc');
  const [txnFilterType, setTxnFilterType] = useState<'ALL' | 'CREDIT' | 'DEBIT'>('ALL');
  const [txnFilterCategory, setTxnFilterCategory] = useState<string>('ALL');
  const [txnSearchTerm, setTxnSearchTerm] = useState('');
  const [txnDeepAnalysisRunning, setTxnDeepAnalysisRunning] = useState(false);
  const [txnDeepAnalysisResult, setTxnDeepAnalysisResult] = useState<string | null>(null);
  const [applicantAnalyticsModalOpen, setApplicantAnalyticsModalOpen] = useState(false);
  const [analysisHistoryModalOpen, setAnalysisHistoryModalOpen] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState<any[]>([]);
  const [selectedHistoryJob, setSelectedHistoryJob] = useState<any | null>(null);
  const [selectedApplicantAnalytics, setSelectedApplicantAnalytics] = useState<IBVDecision | null>(null);
  const [historyTimerTick, setHistoryTimerTick] = useState(0);
  const [thresholds, setThresholds] = useState<Record<string, number>>({});
  const [editedThresholds, setEditedThresholds] = useState<Record<string, number>>({});
  const [savingThresholds, setSavingThresholds] = useState(false);

  // Notification system state (using global store)
  const {
    notifications: allNotifications,
    unreadCount: globalUnreadCount,
    markAsRead: globalMarkAsRead,
    addNotification
  } = useNotificationStore();
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);

  // Filter notifications for IBV decisions only
  const ibvNotifications = allNotifications.filter(n => n.metadata?.decisionId);

  // Fetch thresholds
  useEffect(() => {
    const fetchThresholds = async () => {
      try {
        const apiBase = window.location.origin;
        const res = await fetch(`${apiBase}/api/ibv/thresholds`, {
          headers: { 'X-API-Key': 'synapse-dev' }
        });
        if (res.ok) {
          const data = await res.json();
          const thresholdsMap = data.reduce((acc: Record<string, number>, item: any) => {
            acc[item.metric_name] = item.threshold_value;
            return acc;
          }, {});
          setThresholds(thresholdsMap);
          setEditedThresholds(thresholdsMap);
        }
      } catch (err) {
        console.error('Failed to fetch thresholds:', err);
      }
    };
    fetchThresholds();
  }, []);

  // Fetch model info
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const apiBase = window.location.origin;
        const res = await fetch(`${apiBase}/api/ibv/models`, {
          headers: { 'X-API-Key': 'synapse-dev' }
        });

        if (res.ok) {
          const data: ModelsResponse = await res.json();
          setCurrentModel(data.current_model);
          setAvailableModels(data.available_models);
        }
      } catch (error) {
        console.error('Failed to fetch model info:', error);
      }
    };

    fetchModels();
  }, []);

  // Fetch decision statistics from database
  const fetchStats = async () => {
    try {
      console.log('[DEBUG] Fetching stats...');
      const apiBase = window.location.origin;
      const res = await fetch(`${apiBase}/api/ibv/decisions/stats`, {
        headers: { 'X-API-Key': 'synapse-dev' }
      });

      if (res.ok) {
        const stats = await res.json();
        console.log('[DEBUG] Stats received:', stats);
        setDbStats(stats);
      } else {
        console.error('[DEBUG] Stats fetch failed:', res.status);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch decisions function with pagination
  const fetchDecisions = async (append = false) => {
    try {
      if (append) setLoadingMore(true);

      const apiBase = window.location.origin;
      const limit = append ? 10 : Math.max(decisions.length, 10);
      const offset = append ? decisions.length : 0;
      const res = await fetch(`${apiBase}/api/ibv/decisions?limit=${limit}&offset=${offset}`, {
        headers: { 'X-API-Key': 'synapse-dev' }
      });

      if (!res.ok) throw new Error('Failed to fetch decisions');

      const data = await res.json();
      const newDecisions = data.decisions || [];

      if (append) {
        setDecisions(prev => [...prev, ...newDecisions]);
        setHasMore(newDecisions.length === 5);
      } else {
        setDecisions(newDecisions);
        setHasMore(newDecisions.length >= limit);
      }

      setError(null);

      // Fetch stats whenever decisions are updated (but not on append)
      if (!append) fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch categorization rules
  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await fetch('/api/ibv/categorization-rules');
        const data = await response.json();
        if (data.success) {
          setCustomRules(data.rules);
        }
      } catch (error) {
        console.error('Failed to fetch categorization rules:', error);
      }
    };
    fetchRules();
  }, []);

  // Fetch decisions on mount only (websocket/button actions handle updates)
  useEffect(() => {
    fetchDecisions();
  }, []);

  // Fetch queue statistics every 3 seconds
  useEffect(() => {
    const fetchQueueStats = async () => {
      try {
        const res = await fetch(`${window.location.origin}/api/ibv/health`);
        const data = await res.json();
        if (data.queue) {
          setQueueStats(data.queue);
        }
      } catch (error) {
        console.error('[QUEUE-STATS] Error:', error);
      }
    };

    fetchQueueStats(); // Initial fetch
    const interval = setInterval(fetchQueueStats, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Load spec files
  useEffect(() => {
    const loadSpecs = async () => {
      try {
        const response = await fetch('/api/specs');
        if (response.ok) {
          const data = await response.json();
          setSpecs(data);
        }
      } catch (err) {
        console.error('Failed to load specs:', err);
      } finally {
        setSpecsLoading(false);
      }
    };
    loadSpecs();
  }, []);

  // Helper functions for spec downloads
  const downloadSpec = async (filename: string) => {
    try {
      const response = await fetch(`/api/specs/${filename}`);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download spec:', err);
      alert('Failed to download spec file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // WebSocket connection for real-time processing updates
  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/processing`;

    console.log('[ProcessingWS] Connecting to:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[ProcessingWS] Connected');
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        if (message.type === 'stage_update') {
          const { decision_id, stage, status, customer_name, decision, queue_position, queue_depth, active_processing } = message;

          setProcessingStatus(prev => {
            const updated = new Map(prev);
            const current = updated.get(decision_id) || {
              customer_name: customer_name || 'Processing...',
              queued: false,
              queue_position: undefined,
              queue_depth: undefined,
              active_processing: undefined,
              stages: {
                extraction: 'pending' as const,
                analysis: 'pending' as const,
                qa: 'pending' as const,
                assessment: 'pending' as const,
                saving: 'pending' as const
              },
              decision: undefined,
              complete: false
            };

            // Handle queue status
            if (stage === 'queued' && status === 'started') {
              current.queued = true;
              current.queue_position = queue_position;
              current.queue_depth = queue_depth;
              current.active_processing = active_processing;
            }

            // When extraction starts, we're out of queue
            if (stage === 'extraction' && status === 'started') {
              current.queued = false;
            }

            // Update stage status
            if (stage !== 'complete' && stage !== 'queued') {
              current.stages[stage as keyof typeof current.stages] = status as any;
            }

            // Track completion
            if (stage === 'complete' && status === 'completed') {
              current.complete = true;
              current.decision = decision;

              // Mark this decision as newly completed for fade-in animation
              setCompletedDecisions(prev => new Set(prev).add(decision_id));

              // Immediately fetch to get the new decision data
              fetchDecisions();

              // Remove overlay after fade completes (4000ms fade + 800ms delay + buffer)
              setTimeout(() => {
                setProcessingStatus(prev => {
                  const updated = new Map(prev);
                  updated.delete(decision_id);
                  return updated;
                });
                // Remove from completed set after animation finishes
                setTimeout(() => {
                  setCompletedDecisions(prev => {
                    const updated = new Set(prev);
                    updated.delete(decision_id);
                    return updated;
                  });
                }, 1000);
              }, 5500);
            }

            updated.set(decision_id, current);
            return updated;
          });
        }
      } catch (error) {
        console.error('[ProcessingWS] Error parsing message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[ProcessingWS] Error:', error);
    };

    ws.onclose = () => {
      console.log('[ProcessingWS] Disconnected');
    };

    return () => {
      ws.close();
    };
  }, []);

  // Stats calculations (use DB stats for counts, calculate other metrics from loaded decisions)
  const stats = useMemo(() => {
    // Use database stats for total counts
    const { total, approved, declined, review, approvalRate } = dbStats;

    const withLLM = decisions.filter(d => d.llm_recommendation || d.ai_decision);
    const aiApprovals = withLLM.filter(d => d.ai_decision === 'APPROVE').length;
    const aiApprovalRate = withLLM.length > 0 ? (aiApprovals / withLLM.length) * 100 : 0;

    const avgProcessingTime = withLLM.length > 0
      ? withLLM.reduce((sum, d) => sum + (d.llm_processing_time_ms || 0), 0) / withLLM.length / 1000
      : 0;

    const avgIncome = decisions.filter(d => d.income_analysis?.monthly_income || d.monthly_income_avg).length > 0
      ? decisions.reduce((sum, d) => sum + (d.income_analysis?.monthly_income || d.monthly_income_avg || 0), 0) / decisions.filter(d => d.income_analysis?.monthly_income || d.monthly_income_avg).length
      : 0;

    return { total, approved, declined, review, approvalRate, aiApprovalRate, avgProcessingTime, avgIncome };
  }, [decisions, dbStats]);

  // Notification handlers
  const handleNotificationClick = (notification: typeof allNotifications[0]) => {
    globalMarkAsRead(notification.id);
    const decisionId = notification.metadata?.decisionId as string;
    const decision = decisions.find(d => d.id === decisionId);
    if (decision) {
      setSelectedReviewDecision(decision);
      setReviewModalOpen(true);
    }
    setNotificationDropdownOpen(false);
  };

  const unreadIbvCount = ibvNotifications.filter(n => !n.read).length;

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target as Node)) {
        setNotificationDropdownOpen(false);
      }
    };

    if (notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationDropdownOpen]);

  // Timer for updating elapsed time on running analysis jobs
  useEffect(() => {
    if (!analysisHistoryModalOpen) return;

    const hasRunningJobs = analysisHistory.some(job => job.status === 'running');
    if (!hasRunningJobs) return;

    const interval = setInterval(() => {
      setHistoryTimerTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [analysisHistoryModalOpen, analysisHistory]);

  // Update AI reasoning modal when decisions change
  useEffect(() => {
    if (selectedAiReasoning && decisions.length > 0) {
      const updatedDecision = decisions.find(d => d.id === selectedAiReasoning.decision.id);
      if (updatedDecision) {
        setSelectedAiReasoning({
          ...selectedAiReasoning,
          decision: updatedDecision,
          reasoning: updatedDecision.senior_assessment?.narrative_summary || updatedDecision.risk_assessment?.summary || updatedDecision.ai_full_analysis || updatedDecision.llm_reasoning || selectedAiReasoning.reasoning
        });
      }
    }
  }, [decisions]);

  // Update report modal when decisions change
  useEffect(() => {
    if (selectedReportDecision && decisions.length > 0) {
      const updatedDecision = decisions.find(d => d.id === selectedReportDecision.id);
      if (updatedDecision) {
        setSelectedReportDecision(updatedDecision);
      }
    }
  }, [decisions]);

  // Update transaction modal when decisions change
  useEffect(() => {
    if (selectedTransactionDecision && decisions.length > 0) {
      const updatedDecision = decisions.find(d => d.id === selectedTransactionDecision.id);
      if (updatedDecision) {
        setSelectedTransactionDecision(updatedDecision);
      }
    }
  }, [decisions]);

  // Update review modal when decisions change
  useEffect(() => {
    if (selectedReviewDecision && decisions.length > 0) {
      const updatedDecision = decisions.find(d => d.id === selectedReviewDecision.id);
      if (updatedDecision) {
        setSelectedReviewDecision(updatedDecision);
      }
    }
  }, [decisions]);

  // Update client analytics modal when decisions change
  useEffect(() => {
    if (selectedClientDecision && decisions.length > 0) {
      const updatedDecision = decisions.find(d => d.id === selectedClientDecision.id);
      if (updatedDecision) {
        setSelectedClientDecision(updatedDecision);
      }
    }
  }, [decisions]);

  // Decline reasons data
  const declineReasonsData = useMemo(() => {
    const reasons = {
      noJob: 0,
      highNSF: 0,
      bounces: 0,
      highFees: 0,
      unaffordable: 0,
      overleveraged: 0
    };

    decisions.filter(d => !d.approved).forEach(decision => {
      if (decision.criteria) {
        if (!decision.criteria.hasJob) reasons.noJob++;
        if (!decision.criteria.lowNSFRate) reasons.highNSF++;
        if (!decision.criteria.paysLendersBack) reasons.bounces++;
        if (!decision.criteria.reasonableFees) reasons.highFees++;
        if (!decision.criteria.loanAffordable) reasons.unaffordable++;
        if (!decision.criteria.notOverleveraged) reasons.overleveraged++;
      }
    });

    return [
      { name: '💼 No Employment', value: reasons.noJob, color: '#ef4444' },
      { name: '📉 High NSF Rate', value: reasons.highNSF, color: '#f59e0b' },
      { name: '💳 Payment Bounces', value: reasons.bounces, color: '#ec4899' },
      { name: '💸 Excessive Fees', value: reasons.highFees, color: '#f97316' },
      { name: '🏦 Loan Too Large', value: reasons.unaffordable, color: '#8b5cf6' },
      { name: '⚖️ Over-leveraged', value: reasons.overleveraged, color: '#06b6d4' }
    ].filter(r => r.value > 0);
  }, [decisions]);

  // Criteria pass rate data
  const criteriaPassRateData = useMemo(() => {
    if (decisions.length === 0) return [];

    const counts = {
      hasJob: 0,
      lowNSFRate: 0,
      paysLendersBack: 0,
      reasonableFees: 0,
      loanAffordable: 0,
      notOverleveraged: 0
    };

    decisions.forEach(d => {
      if (d.criteria) {
        if (d.criteria.hasJob) counts.hasJob++;
        if (d.criteria.lowNSFRate) counts.lowNSFRate++;
        if (d.criteria.paysLendersBack) counts.paysLendersBack++;
        if (d.criteria.reasonableFees) counts.reasonableFees++;
        if (d.criteria.loanAffordable) counts.loanAffordable++;
        if (d.criteria.notOverleveraged) counts.notOverleveraged++;
      }
    });

    return [
      { name: '💼 Employment', rate: (counts.hasJob / decisions.length) * 100, color: '#10b981' },
      { name: '📊 Low NSF Rate', rate: (counts.lowNSFRate / decisions.length) * 100, color: '#3b82f6' },
      { name: '💳 Repayment', rate: (counts.paysLendersBack / decisions.length) * 100, color: '#8b5cf6' },
      { name: '💰 Fee Control', rate: (counts.reasonableFees / decisions.length) * 100, color: '#f59e0b' },
      { name: '🏦 Affordability', rate: (counts.loanAffordable / decisions.length) * 100, color: '#06b6d4' },
      { name: '⚖️ Leverage', rate: (counts.notOverleveraged / decisions.length) * 100, color: '#ec4899' }
    ];
  }, [decisions]);

  // Approval trend (last 20 decisions)
  const approvalTrendData = useMemo(() => {
    const recent = decisions.slice(0, 20).reverse();
    let approved = 0;
    let total = 0;

    return recent.map((d, idx) => {
      total++;
      if (d.approved) approved++;
      return {
        index: idx + 1,
        rate: (approved / total) * 100,
        approved: d.approved ? 1 : 0
      };
    });
  }, [decisions]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getCriteriaIcon = (passed: boolean) => {
    return passed ? (
      <CheckCircle className="w-4 h-4 text-green-400" />
    ) : (
      <XCircle className="w-4 h-4 text-red-400" />
    );
  };

  const handleVerboseTest = async (refId: string) => {
    setVerboseLoading(true);
    try {
      const apiBase = window.location.origin;
      const res = await fetch(`${apiBase}/api/ibv/verbose-test/${refId}`, {
        headers: { 'X-API-Key': 'synapse-dev' }
      });

      if (res.ok) {
        const data = await res.json();
        setVerboseData(data);
      }
    } catch (error) {
      console.error('Verbose test failed:', error);
    } finally {
      setVerboseLoading(false);
    }
  };

  const handleReviewClick = (decision: IBVDecision) => {
    setSelectedReviewDecision(decision);
    setReviewModalOpen(true);
  };

  const handleApproveDeclineReview = async (approve: boolean) => {
    if (!selectedReviewDecision) return;

    setReviewUpdating(true);
    try {
      const serviceKey = import.meta.env.VITE_SUPABASE_SERVICE_KEY;
      const response = await fetch(
        `https://symgjyicrpuixzeoxxgl.supabase.co/rest/v1/ibv_decisions?id=eq.${selectedReviewDecision.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': serviceKey,
            'Authorization': `Bearer ${serviceKey}`,
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify({
            decision: approve ? 'APPROVED' : 'DECLINE',
            requires_review: false
          })
        }
      );

      if (response.ok) {
        // Refresh data from backend to show updated decision
        await fetchDecisions();
        // Delay to let DB/cache propagate before fetching stats
        setTimeout(() => fetchStats(), 500);
        setReviewModalOpen(false);
        setSelectedReviewDecision(null);
      } else {
        console.error('Failed to update decision:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Failed to update decision:', error);
    } finally {
      setReviewUpdating(false);
    }
  };

  const handleDeepAnalysis = async (decisionOverride?: IBVDecision) => {
    if (deepAnalysisRunning) return; // Prevent double-click

    // Use override decision if provided, otherwise use selectedReviewDecision
    const targetDecision = decisionOverride || selectedReviewDecision;
    if (!targetDecision) return;

    // Update selectedReviewDecision if using override (for modal display)
    if (decisionOverride) {
      setSelectedReviewDecision(decisionOverride);
    }

    setDeepAnalysisRunning(true);
    setDeepAnalysisResult(null);
    setDeepAnalysisTimer(0);

    // Add processing notification immediately
    addNotification({
      type: 'task_started',
      priority: 'normal',
      title: '🔬 Processing Deep Analysis',
      message: `${targetDecision.customer_name} - Running AI audit...`,
      metadata: {
        decisionId: targetDecision.id,
        customerName: targetDecision.customer_name
      }
    });

    // Start timer (update every 100ms for smooth decimal display)
    const timerInterval = setInterval(() => {
      setDeepAnalysisTimer(prev => prev + 0.1);
    }, 100);

    try {
      const response = await fetch('/api/ibv/deep-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision_id: targetDecision.id,
          raw_ibv_data: targetDecision.raw_ibv_data,
          rust_decision: targetDecision.decision,
          rust_reasons: targetDecision.reasons,
          ai_decision: targetDecision.ai_decision,
          ai_summary: targetDecision.ai_summary,
          rust_metrics: {
            nsf_count: targetDecision.nsf_count,
            total_fees: targetDecision.total_fees,
            monthly_income: targetDecision.monthly_income_avg,
            lender_count: targetDecision.lender_count,
            loan_load_pct: targetDecision.loan_load_pct,
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        setDeepAnalysisResult(result.findings || result.message || 'Analysis complete');
        // Add notification when deep analysis completes
        addNotification({
          type: 'info',
          title: 'Deep Analysis Complete',
          message: `Deep Analysis complete for ${targetDecision.customer_name}`,
          metadata: {
            decisionId: targetDecision.id,
            customerName: targetDecision.customer_name,
          },
        });
      } else {
        setDeepAnalysisResult('Deep analysis failed - check console');
        addNotification({
          type: 'error',
          priority: 'high',
          title: '❌ Deep Analysis Failed',
          message: `${targetDecision.customer_name} - Analysis request failed`,
          metadata: {
            decisionId: targetDecision.id,
            customerName: targetDecision.customer_name
          }
        });
      }
    } catch (error) {
      console.error('Deep analysis error:', error);
      setDeepAnalysisResult('Error running deep analysis');
      addNotification({
        type: 'error',
        priority: 'high',
        title: '❌ Deep Analysis Error',
        message: `${targetDecision.customer_name} - ${error instanceof Error ? error.message : 'Unknown error'}`,
        metadata: {
          decisionId: targetDecision.id,
          customerName: targetDecision.customer_name
        }
      });
    } finally {
      clearInterval(timerInterval);
      setDeepAnalysisRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Skeleton Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>

          {/* Skeleton Graphs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-32 mb-4"></div>
                <div className="h-64 bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6 flex items-center justify-center">
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-6 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <h3 className="text-xl font-semibold text-red-400 mb-2">Error Loading Decisions</h3>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-scroll bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <style>{`
        @keyframes shine-orange {
          0% { box-shadow: 0 0 20px rgba(249, 115, 22, 0.6), 0 0 40px rgba(249, 115, 22, 0.3), inset 0 0 20px rgba(249, 115, 22, 0.1); }
          25% { box-shadow: 0 0 30px rgba(249, 115, 22, 0.8), 0 0 60px rgba(249, 115, 22, 0.4), inset 0 0 30px rgba(249, 115, 22, 0.2); }
          50% { box-shadow: 0 0 40px rgba(249, 115, 22, 1), 0 0 80px rgba(249, 115, 22, 0.5), inset 0 0 40px rgba(249, 115, 22, 0.3); }
          75% { box-shadow: 0 0 30px rgba(249, 115, 22, 0.8), 0 0 60px rgba(249, 115, 22, 0.4), inset 0 0 30px rgba(249, 115, 22, 0.2); }
          100% { box-shadow: 0 0 20px rgba(249, 115, 22, 0.6), 0 0 40px rgba(249, 115, 22, 0.3), inset 0 0 20px rgba(249, 115, 22, 0.1); }
        }
        @keyframes shine-purple {
          0% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(168, 85, 247, 0.3), inset 0 0 20px rgba(168, 85, 247, 0.1); }
          25% { box-shadow: 0 0 30px rgba(168, 85, 247, 0.8), 0 0 60px rgba(168, 85, 247, 0.4), inset 0 0 30px rgba(168, 85, 247, 0.2); }
          50% { box-shadow: 0 0 40px rgba(168, 85, 247, 1), 0 0 80px rgba(168, 85, 247, 0.5), inset 0 0 40px rgba(168, 85, 247, 0.3); }
          75% { box-shadow: 0 0 30px rgba(168, 85, 247, 0.8), 0 0 60px rgba(168, 85, 247, 0.4), inset 0 0 30px rgba(168, 85, 247, 0.2); }
          100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(168, 85, 247, 0.3), inset 0 0 20px rgba(168, 85, 247, 0.1); }
        }
      `}</style>
      {/* Sticky Header Bar */}
      <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-yellow-400" />
            <h1 className="text-xl font-bold text-white">IBV Approval Pipeline</h1>
            {queueStats && (
              <div className="flex items-center gap-2 px-3 py-1 bg-gray-800/50 border border-gray-700 rounded-lg">
                <div className="text-xs text-gray-400">Queue:</div>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-semibold text-green-400">{queueStats.active}</span>
                  <span className="text-xs text-gray-500">/</span>
                  <span className="text-xs font-semibold text-yellow-400">{queueStats.queued}</span>
                  <span className="text-xs text-gray-500">({queueStats.limit} max)</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-gray-400">Current Model</div>
              <div className="text-sm font-semibold text-blue-400">{currentModel || 'Loading...'}</div>
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await fetch('/api/ibv/test-webhook', { method: 'POST' });
                  const data = await res.json();
                  if (data.success) {
                    console.log('[TEST] Sent test webhook:', data.test_name);
                  } else {
                    console.error('[TEST] Failed:', data.error);
                  }
                } catch (error) {
                  console.error('[TEST] Error:', error);
                }
              }}
              className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 hover:border-blue-400 rounded-lg text-blue-400 text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95"
              title="Send random test webhook"
            >
              Send Test
            </button>
            <button
              onClick={async () => {
                if (!confirm('Delete all TEST- data? This cannot be undone.')) return;
                try {
                  const res = await fetch('/api/ibv/test-data', { method: 'DELETE' });
                  const data = await res.json();
                  if (data.success) {
                    alert(`Deleted ${data.deleted} test records`);
                    fetchDecisions();
                  } else {
                    alert('Failed to delete test data');
                  }
                } catch (error) {
                  console.error('[TEST-DELETE] Error:', error);
                  alert('Error deleting test data');
                }
              }}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 hover:border-red-400 rounded-lg text-red-400 text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95"
              title="Delete all test data (TEST-*)"
            >
              Clear Tests
            </button>
            <div className="relative" ref={notificationDropdownRef}>
                <button
                  onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                  className="relative p-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 hover:border-gray-600 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5 text-gray-400" />
                  {unreadIbvCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadIbvCount}
                    </span>
                  )}
                </button>

              {/* Notification Dropdown */}
              {notificationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
                  <div className="p-4 border-b border-gray-700">
                    <h3 className="text-white font-semibold">IBV Notifications</h3>
                    <p className="text-xs text-gray-400 mt-1">{unreadIbvCount} unread</p>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {ibvNotifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        No notifications yet
                      </div>
                    ) : (
                      ibvNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-4 border-b border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors ${
                            !notification.read ? 'bg-blue-500/10' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={`text-sm font-semibold ${!notification.read ? 'text-white' : 'text-gray-300'}`}>
                                {notification.title}
                              </p>
                              <p className={`text-sm ${!notification.read ? 'text-gray-300' : 'text-gray-400'}`}>
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(notification.timestamp).toLocaleString()}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={async () => {
                setAnalysisHistoryModalOpen(true);
                try {
                  const res = await fetch('/api/ibv/analysis-history');
                  const data = await res.json();
                  setAnalysisHistory(data);
                } catch (error) {
                  console.error('[HISTORY] Failed to load:', error);
                }
              }}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 hover:border-gray-600 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 group"
              title="Analysis History"
            >
              <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              onClick={() => setSettingsModalOpen(true)}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 hover:border-gray-600 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 group"
              title="LLM Settings"
            >
              <svg className="w-5 h-5 text-gray-400 group-hover:text-white group-hover:rotate-90 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Banner */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div
            className="group bg-gradient-to-br from-blue-500/10 to-blue-600/5 backdrop-blur-sm border border-blue-500/20 rounded-xl p-6 hover:border-blue-500/40 transition-all duration-300 hover:scale-105 cursor-pointer"
            onClick={() => setAnalyticsModalOpen('decisions')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-blue-400">Total Decisions</div>
              <Activity className="w-5 h-5 text-blue-400 opacity-50 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-gray-400 mt-2">Click to view trends</div>
          </div>

          <div
            className="group bg-gradient-to-br from-green-500/10 to-green-600/5 backdrop-blur-sm border border-green-500/20 rounded-xl p-6 hover:border-green-500/40 transition-all duration-300 hover:scale-105 cursor-pointer"
            onClick={() => setDecisionFilter(decisionFilter === 'approved' ? 'all' : 'approved')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-green-400">Total Approved</div>
              <CheckCircle className="w-5 h-5 text-green-400 opacity-50 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="text-3xl font-bold text-white">{stats.approved}</div>
            <div className="text-xs text-gray-400 mt-2">Click to {decisionFilter === 'approved' ? 'show all' : 'filter'}</div>
          </div>

          <div
            className="group bg-gradient-to-br from-red-500/10 to-red-600/5 backdrop-blur-sm border border-red-500/20 rounded-xl p-6 hover:border-red-500/40 transition-all duration-300 hover:scale-105 cursor-pointer"
            onClick={() => setDecisionFilter(decisionFilter === 'declined' ? 'all' : 'declined')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-red-400">Total Declined</div>
              <XCircle className="w-5 h-5 text-red-400 opacity-50 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="text-3xl font-bold text-white">{stats.declined}</div>
            <div className="text-xs text-gray-400 mt-2">Click to {decisionFilter === 'declined' ? 'show all' : 'filter'}</div>
          </div>

          <div
            className="group bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 backdrop-blur-sm border border-yellow-500/20 rounded-xl p-6 hover:border-yellow-500/40 transition-all duration-300 hover:scale-105 cursor-pointer"
            onClick={() => setDecisionFilter(decisionFilter === 'review' ? 'all' : 'review')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-yellow-400">Under Review</div>
              <AlertCircle className="w-5 h-5 text-yellow-400 opacity-50 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="text-3xl font-bold text-white">{stats.review}</div>
            <div className="text-xs text-gray-400 mt-2">Click to {decisionFilter === 'review' ? 'show all' : 'filter'}</div>
          </div>

          <div
            className="group bg-gradient-to-br from-purple-500/10 to-purple-600/5 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 hover:border-purple-500/40 transition-all duration-300 hover:scale-105 cursor-pointer"
            onClick={() => setAnalyticsModalOpen('rate')}
            title="Percentage of applications approved vs declined - click to view trends over time"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-purple-400">Approval Rate</div>
              <Target className="w-5 h-5 text-purple-400 opacity-50 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="text-3xl font-bold text-white">
              {stats.approvalRate.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-400 mt-2">
              Click to view trends
            </div>
          </div>
        </div>
      </div>

    {/* Section 2: Recent Decisions */}
    <section className="flex flex-col p-6">
      <div className="max-w-7xl mx-auto w-full h-full flex flex-col">
        {/* Recent Decisions */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-lg font-semibold text-white">Recent Decisions</h3>
            {decisionFilter !== 'all' && (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                  decisionFilter === 'review' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40' :
                  decisionFilter === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/40' :
                  'bg-red-500/20 text-red-400 border border-red-500/40'
                }`}>
                  Showing: {decisionFilter.toUpperCase()}
                </span>
                <button
                  onClick={() => setDecisionFilter('all')}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors"
                >
                  Clear Filter
                </button>
              </div>
            )}
          </div>

          {decisions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>No IBV decisions yet</p>
            </div>
          ) : (
            <div className="space-y-3 pr-2 overflow-y-auto flex-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* Processing-only overlays (for decisions not yet in the list) */}
              {Array.from(processingStatus.entries())
                .filter(([decisionId]) => !decisions.find(d => d.id === decisionId))
                .map(([decisionId, status]) => (
                  <div key={`processing-only-${decisionId}`} className="relative">
                    {/* Placeholder card skeleton - same size as real card */}
                    <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 min-h-[400px]">
                      <div className="animate-pulse">
                        <div className="h-6 bg-gray-700/50 rounded w-3/4 mb-4"></div>
                        <div className="space-y-3">
                          <div className="h-4 bg-gray-700/50 rounded w-full"></div>
                          <div className="h-4 bg-gray-700/50 rounded w-5/6"></div>
                          <div className="h-4 bg-gray-700/50 rounded w-4/6"></div>
                        </div>
                      </div>
                    </div>

                    {/* Processing overlay on top */}
                    <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-sm border border-blue-500/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                          <h4 className="font-bold text-white">{status.customer_name}</h4>
                        </div>
                        <span className="text-xs text-blue-400 font-medium">
                          {status.queued ? 'Queued' : 'Processing...'}
                        </span>
                      </div>

                      {status.queued && (
                        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm text-yellow-400 font-medium">Waiting in Queue</div>
                              <div className="text-xs text-gray-400 mt-0.5">
                                Position {status.queue_position} of {status.queue_depth}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Active</div>
                              <div className="text-sm text-white font-medium">{status.active_processing}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                            status.stages.extraction === 'completed' ? 'bg-green-500/20 border-green-500' :
                            status.stages.extraction === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                            'bg-gray-700/20 border-gray-600'
                          }`}>
                            {status.stages.extraction === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                            {status.stages.extraction === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                          </div>
                          <div className="flex-1"><div className="text-sm text-gray-300">Data Extraction</div></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                            status.stages.analysis === 'completed' ? 'bg-green-500/20 border-green-500' :
                            status.stages.analysis === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                            'bg-gray-700/20 border-gray-600'
                          }`}>
                            {status.stages.analysis === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                            {status.stages.analysis === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                          </div>
                          <div className="flex-1"><div className="text-sm text-gray-300">Risk Analysis</div></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                            status.stages.qa === 'completed' ? 'bg-green-500/20 border-green-500' :
                            status.stages.qa === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                            'bg-gray-700/20 border-gray-600'
                          }`}>
                            {status.stages.qa === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                            {status.stages.qa === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                          </div>
                          <div className="flex-1"><div className="text-sm text-gray-300">Quality Assurance</div></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                            status.stages.assessment === 'completed' ? 'bg-green-500/20 border-green-500' :
                            status.stages.assessment === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                            'bg-gray-700/20 border-gray-600'
                          }`}>
                            {status.stages.assessment === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                            {status.stages.assessment === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                          </div>
                          <div className="flex-1"><div className="text-sm text-gray-300">Final Assessment</div></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                            status.stages.saving === 'completed' ? 'bg-green-500/20 border-green-500' :
                            status.stages.saving === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                            'bg-gray-700/20 border-gray-600'
                          }`}>
                            {status.stages.saving === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                            {status.stages.saving === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                          </div>
                          <div className="flex-1"><div className="text-sm text-gray-300">Saving Decision</div></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}


              {/* Decision Cards - with overlay on top if processing */}
              {decisions
                .filter(decision => {
                  if (decisionFilter === 'all') return true;
                  if (decisionFilter === 'review') return decision.decision === 'REVIEW';
                  if (decisionFilter === 'approved') return decision.decision === 'APPROVED';
                  if (decisionFilter === 'declined') return decision.decision === 'DECLINED' || decision.decision === 'DECLINE';
                  return true;
                })
                .map((decision, index) => {
                    const isProcessing = processingStatus.has(decision.id);
                    const status = processingStatus.get(decision.id);

                    return (
                      <div key={decision.id} className="relative">
                        {/* Decision Card - always renders */}
                        <DecisionCard
                          decision={decision}
                          index={index}
                          isNewlyCompleted={completedDecisions.has(decision.id)}
                          selectedReviewDecision={selectedReviewDecision}
                          reviewUpdating={reviewUpdating}
                          handleReviewClick={handleReviewClick}
                          handleApproveDeclineReview={handleApproveDeclineReview}
                          setSelectedReviewDecision={setSelectedReviewDecision}
                          formatTimestamp={formatTimestamp}
                          formatMoney={formatMoney}
                          formatTokens={formatTokens}
                          openDropdownId={openDropdownId}
                          setOpenDropdownId={setOpenDropdownId}
                          activeTab={activeTab}
                          setActiveTab={setActiveTab}
                          setSelectedTransactionDecision={setSelectedTransactionDecision}
                          setTransactionModalOpen={setTransactionModalOpen}
                          setSelectedReportDecision={setSelectedReportDecision}
                          setReportModalOpen={setReportModalOpen}
                          setSelectedUnifiedRawDecision={setSelectedUnifiedRawDecision}
                          setUnifiedRawDataModalOpen={setUnifiedRawDataModalOpen}
                          setSelectedAiReasoning={setSelectedAiReasoning}
                          setAiModalOpen={setAiModalOpen}
                          addNotification={addNotification}
                          fetchDecisions={fetchDecisions}
                        />

                        {/* Processing Overlay - only if this decision is processing */}
                        {isProcessing && status && (
                          <div
                            key={`overlay-${decision.id}-${status.complete ? 'fading' : 'active'}`}
                            className={`absolute inset-0 bg-gray-900/95 backdrop-blur-sm border border-blue-500/50 rounded-xl p-4 ${
                              status.complete ? 'animate-overlay-fade' : ''
                            }`}
                            style={{ transform: 'translateZ(0)' }}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                <h4 className="font-bold text-white">{status.customer_name}</h4>
                              </div>
                              <span className="text-xs text-blue-400 font-medium">
                                {status.queued ? 'Queued' : 'Processing...'}
                              </span>
                            </div>

                            {status.queued && (
                              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-sm text-yellow-400 font-medium">Waiting in Queue</div>
                                    <div className="text-xs text-gray-400 mt-0.5">
                                      Position {status.queue_position} of {status.queue_depth}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-xs text-gray-400">Active</div>
                                    <div className="text-sm text-white font-medium">{status.active_processing}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                  status.stages.extraction === 'completed' ? 'bg-green-500/20 border-green-500' :
                                  status.stages.extraction === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                                  'bg-gray-700/20 border-gray-600'
                                }`}>
                                  {status.stages.extraction === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                                  {status.stages.extraction === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                                </div>
                                <div className="flex-1"><div className="text-sm text-gray-300">Data Extraction</div></div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                  status.stages.analysis === 'completed' ? 'bg-green-500/20 border-green-500' :
                                  status.stages.analysis === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                                  'bg-gray-700/20 border-gray-600'
                                }`}>
                                  {status.stages.analysis === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                                  {status.stages.analysis === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                                </div>
                                <div className="flex-1"><div className="text-sm text-gray-300">Risk Analysis</div></div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                  status.stages.qa === 'completed' ? 'bg-green-500/20 border-green-500' :
                                  status.stages.qa === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                                  'bg-gray-700/20 border-gray-600'
                                }`}>
                                  {status.stages.qa === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                                  {status.stages.qa === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                                </div>
                                <div className="flex-1"><div className="text-sm text-gray-300">Quality Assurance</div></div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                  status.stages.assessment === 'completed' ? 'bg-green-500/20 border-green-500' :
                                  status.stages.assessment === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                                  'bg-gray-700/20 border-gray-600'
                                }`}>
                                  {status.stages.assessment === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                                  {status.stages.assessment === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                                </div>
                                <div className="flex-1"><div className="text-sm text-gray-300">Final Assessment</div></div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                  status.stages.saving === 'completed' ? 'bg-green-500/20 border-green-500' :
                                  status.stages.saving === 'started' ? 'bg-blue-500/20 border-blue-500 animate-pulse' :
                                  'bg-gray-700/20 border-gray-600'
                                }`}>
                                  {status.stages.saving === 'completed' && <span className="text-green-400 text-sm">✓</span>}
                                  {status.stages.saving === 'started' && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                                </div>
                                <div className="flex-1"><div className="text-sm text-gray-300">Saving Decision</div></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
              })}

              {/* Load More Button */}
              {hasMore && !loading && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={() => fetchDecisions(true)}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                  >
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        @keyframes overlayFadeOut {
          0% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }

        .animate-overlay-fade {
          animation: overlayFadeOut 4s ease-in-out 0.8s forwards;
        }

        /* Flash animation for chart titles */
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .animate-flash-hover:hover {
          animation: flash 0.6s ease-in-out 2;
        }

        /* Random flash animation */
        @keyframes random-flash {
          0%, 90%, 100% { opacity: 1; }
          94%, 96% { opacity: 0.5; }
        }

        .animate-flash-hover {
          animation: random-flash 12s ease-in-out infinite;
        }

        .animate-flash-hover:nth-child(2) {
          animation-delay: 3s;
        }

        .animate-flash-hover:nth-child(3) {
          animation-delay: 7s;
        }

        /* Recharts tooltip styling - white text */
        .recharts-tooltip-wrapper {
          outline: none !important;
        }

        .recharts-default-tooltip {
          background-color: rgba(17, 24, 39, 0.95) !important;
          border: 1px solid rgba(75, 85, 99, 0.5) !important;
          border-radius: 8px !important;
          padding: 8px !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
        }

        .recharts-tooltip-label {
          color: #ffffff !important;
          font-weight: 600 !important;
          margin-bottom: 4px !important;
        }

        .recharts-tooltip-item {
          color: #ffffff !important;
        }

        .recharts-tooltip-item-name,
        .recharts-tooltip-item-separator,
        .recharts-tooltip-item-value {
          color: #ffffff !important;
        }

        /* Bar chart glow effect on hover */
        .bar-glow {
          transition: all 0.3s ease-out;
          filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
        }

        .bar-glow:hover {
          filter: drop-shadow(0 0 12px currentColor)
                  drop-shadow(0 0 20px currentColor)
                  brightness(1.2);
          transform: scaleY(1.05);
          transform-origin: left center;
        }

        /* Remove white background hover on bar charts */
        .recharts-bar-rectangle:hover {
          fill-opacity: 1 !important;
        }

        .recharts-active-bar {
          fill-opacity: 1 !important;
        }

        .recharts-rectangle.recharts-tooltip-cursor {
          fill: none !important;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.3);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(107, 114, 128, 0.5);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(107, 114, 128, 0.7);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>

      {/* AI Analysis Modal */}
      {aiModalOpen && selectedAiReasoning && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setAiModalOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-blue-500/30 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl shadow-blue-500/20 animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-b border-blue-500/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🤖</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">AI Analysis</h3>
                    <p className="text-xs text-gray-400">{selectedAiReasoning.decision.customer_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setAiModalOpen(false)}
                  className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              <div className="flex items-center gap-2">
                {selectedAiReasoning.decision.ai_decision && (
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    selectedAiReasoning.decision.ai_decision === 'APPROVE'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : selectedAiReasoning.decision.ai_decision === 'DECLINE'
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    {selectedAiReasoning.decision.ai_decision === 'APPROVE' ? '✓ AI: APPROVE' : selectedAiReasoning.decision.ai_decision === 'DECLINE' ? '✗ AI: DECLINE' : '⚠ AI: REVIEW'}
                  </span>
                )}
                {selectedAiReasoning.decision.llm_recommendation && (
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    selectedAiReasoning.decision.llm_recommendation === 'APPROVE'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {selectedAiReasoning.decision.llm_recommendation}
                  </span>
                )}
                {selectedAiReasoning.decision.llm_processing_time_ms && (
                  <span className="text-xs px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 font-medium ml-auto">
                    {(selectedAiReasoning.decision.llm_processing_time_ms / 1000).toFixed(1)}s processing
                  </span>
                )}
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)] custom-scrollbar">
              <div className="prose prose-invert prose-sm max-w-none">
                <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {selectedAiReasoning.reasoning}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Trends Modal */}
      {analyticsModalOpen && (() => {
        // Group decisions by date and count
        const groupByDate = (filterFn?: (d: IBVDecision) => boolean) => {
          const grouped = decisions.reduce((acc, d) => {
            const date = new Date(d.timestamp).toLocaleDateString();
            if (!acc[date]) acc[date] = { total: 0, count: 0 };
            acc[date].total++;
            if (!filterFn || filterFn(d)) acc[date].count++;
            return acc;
          }, {} as Record<string, { total: number; count: number }>);

          return Object.entries(grouped)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
            .slice(-30)
            .map(([date, data]) => ({ date, ...data }));
        };

        const getModalConfig = () => {
          switch (analyticsModalOpen) {
            case 'decisions':
              return {
                title: 'Decision Trends',
                icon: '📊',
                color: 'blue',
                data: groupByDate().map(d => ({ date: d.date, value: d.total }))
              };
            case 'approved':
              return {
                title: 'Approval Trends',
                icon: '✅',
                color: 'green',
                data: groupByDate(d => d.approved).map(d => ({ date: d.date, value: d.count }))
              };
            case 'declined':
              return {
                title: 'Decline Trends',
                icon: '❌',
                color: 'red',
                data: groupByDate(d => !d.approved).map(d => ({ date: d.date, value: d.count }))
              };
            case 'rate':
              return {
                title: 'Approval Rate Trends',
                icon: '📈',
                color: 'purple',
                data: groupByDate(d => d.approved).map(d => ({
                  date: d.date,
                  value: d.total > 0 ? (d.count / d.total) * 100 : 0
                }))
              };
            case 'llm-agreement':
              return {
                title: 'AI Approval Trends',
                icon: '🤖',
                color: 'cyan',
                data: groupByDate(d => d.ai_decision === 'APPROVE').map(d => ({
                  date: d.date,
                  value: d.total > 0 ? (d.count / d.total) * 100 : 0
                }))
              };
          }
        };

        const config = getModalConfig()!;
        const gradientColors: Record<string, { from: string; to: string; border: string; stroke: string }> = {
          blue: { from: 'from-blue-500/20', to: 'to-blue-600/10', border: 'border-blue-500/30', stroke: '#3b82f6' },
          green: { from: 'from-green-500/20', to: 'to-green-600/10', border: 'border-green-500/30', stroke: '#10b981' },
          red: { from: 'from-red-500/20', to: 'to-red-600/10', border: 'border-red-500/30', stroke: '#ef4444' },
          purple: { from: 'from-purple-500/20', to: 'to-purple-600/10', border: 'border-purple-500/30', stroke: '#a855f7' },
          cyan: { from: 'from-cyan-500/20', to: 'to-cyan-600/10', border: 'border-cyan-500/30', stroke: '#06b6d4' }
        };
        const colors = gradientColors[config.color];

        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={() => setAnalyticsModalOpen(null)}
          >
            <div
              className={`bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border ${colors.border} rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl shadow-${config.color}-500/20 animate-slideUp`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`bg-gradient-to-r ${colors.from} ${colors.to} border-b ${colors.border} p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 bg-${config.color}-500/20 rounded-lg flex items-center justify-center`}>
                      <span className="text-2xl">{config.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{config.title}</h3>
                      <p className="text-xs text-gray-400">Last 30 decisions</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAnalyticsModalOpen(null)}
                    className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={config.data}>
                    <defs>
                      <linearGradient id={`area-${config.color}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={colors.stroke} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={colors.stroke} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="date"
                      stroke="#9ca3af"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      label={{ value: 'Date', position: 'insideBottom', offset: -20, fill: '#9ca3af' }}
                    />
                    <YAxis
                      stroke="#9ca3af"
                      tick={{ fontSize: 12 }}
                      label={{ value: (analyticsModalOpen === 'rate' || analyticsModalOpen === 'llm-agreement') ? 'Rate (%)' : 'Count', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        border: `1px solid ${colors.stroke}`,
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: any) => [
                        (analyticsModalOpen === 'rate' || analyticsModalOpen === 'llm-agreement') ? `${value.toFixed(1)}%` : value,
                        'Count'
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={colors.stroke}
                      strokeWidth={3}
                      dot={{ fill: colors.stroke, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: colors.stroke }}
                      fill={`url(#area-${config.color})`}
                    />
                  </LineChart>
                </ResponsiveContainer>

                {/* 12-Month Trend Chart - Only for decisions, approved, declined */}
                {(analyticsModalOpen === 'decisions' || analyticsModalOpen === 'approved' || analyticsModalOpen === 'declined') && (() => {
                  const now = new Date();
                  const monthlyData = [];

                  for (let i = 11; i >= 0; i--) {
                    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

                    const monthDecisions = decisions.filter(d => {
                      const decisionDate = new Date(d.timestamp);
                      return decisionDate >= monthStart && decisionDate <= monthEnd;
                    });

                    let value = 0;
                    if (analyticsModalOpen === 'decisions') {
                      value = monthDecisions.length;
                    } else if (analyticsModalOpen === 'approved') {
                      value = monthDecisions.filter(d => d.approved).length;
                    } else if (analyticsModalOpen === 'declined') {
                      value = monthDecisions.filter(d => !d.approved).length;
                    }

                    monthlyData.push({
                      month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                      value
                    });
                  }

                  return (
                    <div className="mt-8 border-t border-gray-700/50 pt-6">
                      <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        12-Month Trend
                      </h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis
                            dataKey="month"
                            stroke="#9ca3af"
                            tick={{ fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            stroke="#9ca3af"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Count', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(17, 24, 39, 0.95)',
                              border: `1px solid ${colors.stroke}`,
                              borderRadius: '8px',
                              color: '#fff'
                            }}
                          />
                          <Bar
                            dataKey="value"
                            fill={colors.stroke}
                            radius={[8, 8, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Client Analytics Modal */}
      {clientAnalyticsOpen && selectedClientDecision && (() => {
        const decision = selectedClientDecision;

        // Prepare financial trends data from decision's time periods
        // Note: Using current NSF count for all periods (no historical data available)
        const currentNSF = decision.fee_analysis?.nsf_count || decision.nsf_count || 0;
        const nsfPerMonth = (currentNSF / 3) || decision.nsf_per_month || 0;

        const riskTrendData = [
          { period: '365d', value: currentNSF, rate: nsfPerMonth },
          { period: '180d', value: currentNSF, rate: nsfPerMonth },
          { period: '90d', value: currentNSF, rate: nsfPerMonth },
          { period: '30d', value: currentNSF, rate: nsfPerMonth }
        ];

        const monthlyIncome = decision.income_analysis?.monthly_income || decision.monthly_income_avg || decision.context?.avgIncome || 0;
        const balanceTrendData = [
          { period: '365d', balance: monthlyIncome * 0.6 },
          { period: '180d', balance: monthlyIncome * 0.8 },
          { period: '90d', balance: monthlyIncome },
          { period: '30d', balance: decision.context?.balanceAfterPayroll || 0 }
        ];

        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={() => setClientAnalyticsOpen(false)}
          >
            <div
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-cyan-500/30 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl shadow-cyan-500/20 animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-b border-cyan-500/30 p-4 sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{decision.customer_name} - Financial Analytics</h3>
                      <p className="text-xs text-gray-400">
                        {decision.approved ? (
                          <span className="text-green-400">✓ APPROVED</span>
                        ) : (
                          <span className="text-red-400">✗ DECLINED</span>
                        )} • Ref: {decision.reference_id}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setClientAnalyticsOpen(false)}
                    className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* NSF Risk Trend Chart */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/5 border border-red-500/20 rounded-lg p-4" title="Tracks NSF (Non-Sufficient Funds) returns over different time periods - shows payment failure patterns">
                  <h4 className="text-sm font-semibold text-red-300 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    🔴 NSF Return History
                  </h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={riskTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="period" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} domain={[0, 'auto']} label={{ value: 'NSF Count', angle: -90, position: 'insideLeft', fill: '#9ca3af' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(17, 24, 39, 0.95)',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value: any) => `${value} NSF returns`}
                      />
                      <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={3} name="NSF Count" dot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-gray-400 mt-2">Total NSF returns: {currentNSF} ({nsfPerMonth.toFixed(1)} per month average)</p>
                </div>

                {/* Balance Trend Chart */}
                <div className="bg-gradient-to-br from-green-500/10 to-blue-500/5 border border-green-500/20 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-300 mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    💰 Income Stability Trend
                  </h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={balanceTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="period" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} label={{ value: 'Balance ($)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(17, 24, 39, 0.95)',
                          border: '1px solid rgba(16, 185, 129, 0.5)',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value: any) => `$${formatMoney(value, 2)}`}
                      />
                      <Line type="monotone" dataKey="balance" stroke="#10b981" strokeWidth={3} name="Balance After Payroll" dot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-gray-400 mt-2">Post-payroll balance indicates financial cushion & stability</p>
                </div>

                {/* Risk Metrics Section */}
                {decision.rust_metrics && decision.rust_metrics.metrics && (
                  <div className="bg-gradient-to-br from-orange-500/10 to-red-500/5 border border-orange-500/20 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-orange-300 mb-4 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      ⚡ Risk Analysis - Metrics vs Thresholds
                    </h4>
                    <div className="space-y-3">
                      {decision.rust_metrics.metrics.map((metric, idx) => {
                        // Skip old format without value/threshold
                        if (metric.value === undefined || metric.threshold === undefined) {
                          return (
                            <div key={idx} className="text-xs text-gray-400">
                              {metric.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {metric.passes ? '✓ Pass' : '✗ Fail'}
                            </div>
                          );
                        }

                        const maxValue = Math.max(metric.value, metric.threshold) * 1.2;
                        const valuePercent = (metric.value / maxValue) * 100;
                        const thresholdPercent = (metric.threshold / maxValue) * 100;
                        const severityColors = {
                          Critical: 'bg-red-500',
                          High: 'bg-orange-500',
                          Moderate: 'bg-yellow-500',
                          Low: 'bg-green-500'
                        };

                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-300 font-medium">
                                {metric.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className={metric.passes ? 'text-green-400' : 'text-red-400'}>
                                  {metric.passes ? '✓' : '✗'}
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded ${severityColors[metric.severity]}/20 text-${metric.severity === 'Critical' ? 'red' : metric.severity === 'High' ? 'orange' : metric.severity === 'Moderate' ? 'yellow' : 'green'}-400`}>
                                  {metric.severity}
                                </span>
                              </div>
                            </div>
                            <div className="relative h-8 bg-gray-800/50 rounded overflow-hidden">
                              <div
                                className={`absolute left-0 top-0 h-full ${metric.passes ? 'bg-green-500/40' : 'bg-red-500/40'} flex items-center px-2 transition-all duration-500`}
                                style={{ width: `${valuePercent}%` }}
                              >
                                <span className="text-[11px] font-medium text-white">
                                  {metric.value.toFixed(2)}
                                </span>
                              </div>
                              <div
                                className="absolute top-0 h-full w-0.5 bg-yellow-400"
                                style={{ left: `${thresholdPercent}%` }}
                                title={`Threshold: ${metric.threshold}`}
                              >
                                <div className="absolute -top-1 -left-2 w-4 h-4 rotate-45 bg-yellow-400"></div>
                              </div>
                            </div>
                            <div className="flex justify-between text-[10px] text-gray-500">
                              <span>Value: {metric.value.toFixed(2)}</span>
                              <span>Threshold: {metric.threshold.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* IBV Report Modal */}
      {reportModalOpen && selectedReportDecision && (() => {
        const decision = selectedReportDecision;
        const etransfer = decision.etransfer_analysis;
        const paydayDrain = decision.payday_drain_analysis;
        const criteriaCount = decision.criteria ? Object.values(decision.criteria).filter(Boolean).length : 0;
        const totalCriteria = decision.criteria ? Object.keys(decision.criteria).length : 0;

        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={() => setReportModalOpen(false)}
          >
            <div
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-orange-500/30 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl shadow-orange-500/20 animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`bg-gradient-to-r ${decision.decision === 'APPROVED' ? 'from-green-500/20 to-emerald-600/10 border-green-500/30' : decision.decision === 'REVIEW' ? 'from-orange-500/20 to-yellow-600/10 border-orange-500/30' : 'from-red-500/20 to-rose-600/10 border-red-500/30'} border-b p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${decision.decision === 'APPROVED' ? 'bg-green-500/20' : decision.decision === 'REVIEW' ? 'bg-orange-500/20' : 'bg-red-500/20'} rounded-lg flex items-center justify-center`}>
                      <FileText className={`w-6 h-6 ${decision.decision === 'APPROVED' ? 'text-green-400' : decision.decision === 'REVIEW' ? 'text-orange-400' : 'text-red-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">IBV Analysis Report</h3>
                      <p className="text-sm text-gray-300">{decision.customer_name}</p>
                      <p className="text-xs text-gray-500">Reference: {decision.reference_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`px-4 py-2 rounded-lg ${decision.decision === 'APPROVED' ? 'bg-green-500/20 text-green-400' : decision.decision === 'REVIEW' ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'} font-bold text-lg`}>
                      {decision.decision}
                    </div>
                    <button
                      onClick={() => setReportModalOpen(false)}
                      className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                {/* Decision Header */}
                <div className={`border rounded-lg p-5 mb-4 ${decision.decision === 'APPROVED' ? 'bg-gradient-to-br from-green-500/5 to-emerald-500/5 border-green-500/30' : decision.decision === 'REVIEW' ? 'bg-gradient-to-br from-orange-500/10 to-yellow-500/5 border-orange-500/30' : 'bg-gradient-to-br from-red-500/5 to-rose-500/5 border-red-500/30'}`}>
                  <h4 className="text-lg font-bold text-white mb-3">Decision Summary</h4>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <p className="text-gray-300 leading-relaxed mb-3">
                      <strong className={decision.decision === 'APPROVED' ? 'text-green-400' : decision.decision === 'REVIEW' ? 'text-orange-400' : 'text-red-400'}>{decision.decision}</strong> - {' '}
                      {decision.confidence_score ? `${decision.confidence_score.toFixed(0)}% confidence` : 'Analysis complete'}. {' '}
                      {decision.decision === 'APPROVED' ? (
                        <>Applicant <strong>{decision.customer_name}</strong> was approved.</>
                      ) : decision.decision === 'REVIEW' ? (
                        <>Applicant <strong>{decision.customer_name}</strong> requires manual review.</>
                      ) : decision.reasons && decision.reasons.length > 0 ? (
                        <>Applicant <strong>{decision.customer_name}</strong> failed <strong>{decision.reasons.length}</strong> {decision.reasons.length === 1 ? 'criterion' : 'criteria'}.</>
                      ) : (
                        <>Applicant <strong>{decision.customer_name}</strong> was declined.</>
                      )}
                    </p>

                    {decision.reasons && decision.reasons.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-semibold text-gray-400 mb-2">Decision Reasons:</h5>
                        <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm">
                          {decision.reasons.map((reason, idx) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Income Analysis */}
                {decision.income_analysis && (
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-blue-300 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Income Analysis
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-green-400">${decision.income_analysis.monthly_income.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Monthly Income</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-blue-400">${decision.income_analysis.payroll_income.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Payroll Income</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-purple-400">${decision.income_analysis.government_income.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Government Income</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-cyan-400">${decision.income_analysis.other_income.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Other Income</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className={`text-lg font-bold ${decision.income_analysis.income_stability > 0.3 ? 'text-red-400' : 'text-green-400'}`}>
                          {(decision.income_analysis.income_stability * 100).toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-400">Income Stability (CoV)</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-white">${decision.income_analysis.median_income.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Median Income</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className={`text-lg font-bold ${decision.income_analysis.high_variance ? 'text-red-400' : 'text-green-400'}`}>
                          {decision.income_analysis.high_variance ? 'Yes' : 'No'}
                        </div>
                        <div className="text-xs text-gray-400">High Variance</div>
                      </div>
                      {decision.income_analysis.outliers_excluded_count > 0 && (
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <div className="text-lg font-bold text-orange-400">{decision.income_analysis.outliers_excluded_count}</div>
                          <div className="text-xs text-gray-400">Outliers Excluded</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Fee Analysis */}
                {decision.fee_analysis && (
                  <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-orange-300 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Fee Analysis
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-red-400">{decision.fee_analysis.nsf_count}</div>
                        <div className="text-xs text-gray-400">NSF Count</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-red-400">${decision.fee_analysis.nsf_fees.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">NSF Fees</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-orange-400">{decision.fee_analysis.overdraft_count}</div>
                        <div className="text-xs text-gray-400">Overdraft Count</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-orange-400">${decision.fee_analysis.overdraft_fees.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Overdraft Fees</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className={`text-lg font-bold ${decision.fee_analysis.total_fees > 500 ? 'text-red-400' : 'text-gray-300'}`}>
                          ${decision.fee_analysis.total_fees.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-400">Total Fees</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lender Analysis - Full Scorecard */}
                {decision.lender_analysis && decision.lender_analysis.lenders && decision.lender_analysis.lenders.length > 0 && (
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-purple-300 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Lender Analysis - Full Scorecard
                    </h4>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-purple-400">{decision.lender_analysis.lenders.length}</div>
                        <div className="text-xs text-gray-400">Total Lenders</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className={`text-lg font-bold ${decision.lender_analysis.total_payday_debt > 2000 ? 'text-red-400' : 'text-gray-300'}`}>
                          ${decision.lender_analysis.total_payday_debt.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-400">Total Payday Debt</div>
                      </div>
                    </div>

                    {/* Lender Table */}
                    <div className="bg-gray-900/50 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-800/50">
                          <tr>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Lender Name</th>
                            <th className="px-3 py-2 text-center text-gray-400 font-medium">Grade</th>
                            <th className="px-3 py-2 text-right text-gray-400 font-medium">Bounce Count</th>
                            <th className="px-3 py-2 text-right text-gray-400 font-medium">Total Borrowed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {decision.lender_analysis.lenders.map((lender, idx) => (
                            <tr key={idx} className="border-t border-gray-700/50">
                              <td className="px-3 py-2 text-white font-medium">
                                {lender.name.replace(/\s*\(Inverite Categorized\)/gi, '')}
                                {decision.lender_analysis?.highest_risk_lender === lender.name && (
                                  <span className="ml-2 text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Highest Risk</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  lender.grade === 'A' ? 'bg-red-500/30 text-red-400' :
                                  lender.grade === 'B' ? 'bg-orange-500/30 text-orange-400' :
                                  lender.grade === 'C' ? 'bg-yellow-500/30 text-yellow-400' :
                                  'bg-gray-500/30 text-gray-400'
                                }`}>
                                  {lender.grade}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className={`font-bold ${lender.bounce_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                  {lender.bounce_count}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-gray-300">${lender.total_borrowed.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Spending Analysis */}
                {decision.spending_analysis && (
                  <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-cyan-300 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Spending Analysis
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className={`text-lg font-bold ${decision.spending_analysis.risky_spending > 1000 ? 'text-red-400' : 'text-gray-300'}`}>
                          ${decision.spending_analysis.risky_spending?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-gray-400">Risky Spending</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-green-400">${decision.spending_analysis.essential_spending?.toLocaleString() || 0}</div>
                        <div className="text-xs text-gray-400">Essential Spending</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-blue-400">${decision.spending_analysis.discretionary_spending?.toLocaleString() || 0}</div>
                        <div className="text-xs text-gray-400">Discretionary Spending</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-orange-400">{decision.spending_analysis.atm_withdrawal_count || 0}</div>
                        <div className="text-xs text-gray-400">ATM Withdrawals</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-purple-400">${decision.spending_analysis.credit_card_payments?.toLocaleString() || 0}</div>
                        <div className="text-xs text-gray-400">Credit Card Payments</div>
                      </div>
                      {decision.spending_analysis.alcohol_to_groceries_ratio !== undefined && (
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <div className={`text-lg font-bold ${decision.spending_analysis.alcohol_to_groceries_ratio > 0.5 ? 'text-red-400' : 'text-gray-300'}`}>
                            {(decision.spending_analysis.alcohol_to_groceries_ratio * 100).toFixed(1)}%
                          </div>
                          <div className="text-xs text-gray-400">Alcohol/Groceries Ratio</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Trend Analysis */}
                {decision.trend_analysis && (
                  <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-emerald-300 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Trend Analysis
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {decision.trend_analysis.income_trend && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          decision.trend_analysis.income_trend === 'increasing' ? 'bg-green-500/10 border border-green-500/30' :
                          decision.trend_analysis.income_trend === 'decreasing' ? 'bg-red-500/10 border border-red-500/30' :
                          'bg-gray-500/10 border border-gray-500/30'
                        }`}>
                          <span className="text-2xl">
                            {decision.trend_analysis.income_trend === 'increasing' ? '↗️' :
                             decision.trend_analysis.income_trend === 'decreasing' ? '↘️' : '➡️'}
                          </span>
                          <div>
                            <div className="text-xs text-gray-400">Income Trend</div>
                            <div className={`text-sm font-bold ${
                              decision.trend_analysis.income_trend === 'increasing' ? 'text-green-400' :
                              decision.trend_analysis.income_trend === 'decreasing' ? 'text-red-400' :
                              'text-gray-300'
                            }`}>{decision.trend_analysis.income_trend}</div>
                          </div>
                        </div>
                      )}
                      {decision.trend_analysis.nsf_trend && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          decision.trend_analysis.nsf_trend === 'improving' ? 'bg-green-500/10 border border-green-500/30' :
                          decision.trend_analysis.nsf_trend === 'worsening' ? 'bg-red-500/10 border border-red-500/30' :
                          'bg-gray-500/10 border border-gray-500/30'
                        }`}>
                          <span className="text-2xl">
                            {decision.trend_analysis.nsf_trend === 'improving' ? '✅' :
                             decision.trend_analysis.nsf_trend === 'worsening' ? '⚠️' : '➡️'}
                          </span>
                          <div>
                            <div className="text-xs text-gray-400">NSF Trend</div>
                            <div className={`text-sm font-bold ${
                              decision.trend_analysis.nsf_trend === 'improving' ? 'text-green-400' :
                              decision.trend_analysis.nsf_trend === 'worsening' ? 'text-red-400' :
                              'text-gray-300'
                            }`}>{decision.trend_analysis.nsf_trend}</div>
                          </div>
                        </div>
                      )}
                      {decision.trend_analysis.lender_trend && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          decision.trend_analysis.lender_trend === 'decreasing' ? 'bg-green-500/10 border border-green-500/30' :
                          decision.trend_analysis.lender_trend === 'increasing' ? 'bg-red-500/10 border border-red-500/30' :
                          'bg-gray-500/10 border border-gray-500/30'
                        }`}>
                          <span className="text-2xl">
                            {decision.trend_analysis.lender_trend === 'decreasing' ? '↘️' :
                             decision.trend_analysis.lender_trend === 'increasing' ? '↗️' : '➡️'}
                          </span>
                          <div>
                            <div className="text-xs text-gray-400">Lender Trend</div>
                            <div className={`text-sm font-bold ${
                              decision.trend_analysis.lender_trend === 'decreasing' ? 'text-green-400' :
                              decision.trend_analysis.lender_trend === 'increasing' ? 'text-red-400' :
                              'text-gray-300'
                            }`}>{decision.trend_analysis.lender_trend}</div>
                          </div>
                        </div>
                      )}
                      {decision.trend_analysis.balance_trend && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          decision.trend_analysis.balance_trend === 'Improving' ? 'bg-green-500/10 border border-green-500/30' :
                          decision.trend_analysis.balance_trend === 'Declining' || decision.trend_analysis.balance_trend === 'Critical' ? 'bg-red-500/10 border border-red-500/30' :
                          'bg-gray-500/10 border border-gray-500/30'
                        }`}>
                          <span className="text-2xl">
                            {decision.trend_analysis.balance_trend === 'Improving' ? '📈' :
                             decision.trend_analysis.balance_trend === 'Declining' || decision.trend_analysis.balance_trend === 'Critical' ? '📉' : '➡️'}
                          </span>
                          <div>
                            <div className="text-xs text-gray-400">Balance Trend</div>
                            <div className={`text-sm font-bold ${
                              decision.trend_analysis.balance_trend === 'Improving' ? 'text-green-400' :
                              decision.trend_analysis.balance_trend === 'Declining' || decision.trend_analysis.balance_trend === 'Critical' ? 'text-red-400' :
                              'text-gray-300'
                            }`}>{decision.trend_analysis.balance_trend}</div>
                          </div>
                        </div>
                      )}
                      {decision.trend_analysis.spending_trend && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          decision.trend_analysis.spending_trend === 'decreasing' ? 'bg-green-500/10 border border-green-500/30' :
                          decision.trend_analysis.spending_trend === 'increasing' ? 'bg-red-500/10 border border-red-500/30' :
                          'bg-gray-500/10 border border-gray-500/30'
                        }`}>
                          <span className="text-2xl">
                            {decision.trend_analysis.spending_trend === 'decreasing' ? '↘️' :
                             decision.trend_analysis.spending_trend === 'increasing' ? '↗️' : '➡️'}
                          </span>
                          <div>
                            <div className="text-xs text-gray-400">Spending Trend</div>
                            <div className={`text-sm font-bold ${
                              decision.trend_analysis.spending_trend === 'decreasing' ? 'text-green-400' :
                              decision.trend_analysis.spending_trend === 'increasing' ? 'text-red-400' :
                              'text-gray-300'
                            }`}>{decision.trend_analysis.spending_trend}</div>
                          </div>
                        </div>
                      )}
                      {decision.trend_analysis.loan_position_trend && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          decision.trend_analysis.loan_position_trend === 'Improving' ? 'bg-green-500/10 border border-green-500/30' :
                          decision.trend_analysis.loan_position_trend === 'Worsening' || decision.trend_analysis.loan_position_trend === 'Critical' ? 'bg-red-500/10 border border-red-500/30' :
                          'bg-gray-500/10 border border-gray-500/30'
                        }`}>
                          <span className="text-2xl">
                            {decision.trend_analysis.loan_position_trend === 'Improving' ? '✅' :
                             decision.trend_analysis.loan_position_trend === 'Worsening' || decision.trend_analysis.loan_position_trend === 'Critical' ? '⚠️' : '➡️'}
                          </span>
                          <div>
                            <div className="text-xs text-gray-400">Loan Position</div>
                            <div className={`text-sm font-bold ${
                              decision.trend_analysis.loan_position_trend === 'Improving' ? 'text-green-400' :
                              decision.trend_analysis.loan_position_trend === 'Worsening' || decision.trend_analysis.loan_position_trend === 'Critical' ? 'text-red-400' :
                              'text-gray-300'
                            }`}>{decision.trend_analysis.loan_position_trend}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Criteria Summary */}
                {decision.criteria && (
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-400" />
                      Mothership Criteria ({criteriaCount}/{totalCriteria} Pass)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(decision.criteria).map(([key, value]) => (
                      <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${value ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                        {value ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                        <span className={`text-xs ${value ? 'text-green-300' : 'text-red-300'}`}>
                          {key === 'hasJob' && 'Has Job'}
                          {key === 'lowNSFRate' && 'Low NSF'}
                          {key === 'paysLendersBack' && 'Pays Back'}
                          {key === 'reasonableFees' && 'Low Fees'}
                          {key === 'loanAffordable' && 'Affordable'}
                          {key === 'notOverleveraged' && 'Not Overleveraged'}
                          {key === 'noHiddenLenders' && 'No Hidden Lenders'}
                          {key === 'healthyPaydayPattern' && 'Healthy Payday'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* AI Full Analysis */}
                {(decision.ai_full_analysis || decision.llm_reasoning) && (
                  <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border border-cyan-500/30 rounded-lg p-4 mb-4">
                    <h5 className="text-base font-bold text-cyan-300 mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5" /> AI Detailed Risk Analysis
                    </h5>
                    <div className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">
                      {decision.ai_full_analysis || decision.llm_reasoning}
                    </div>
                    {decision.ai_decision && (
                      <div className="mt-3 pt-3 border-t border-cyan-500/20 flex items-center gap-2">
                        <span className="text-xs text-gray-400">AI Decision:</span>
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                          decision.ai_decision === 'APPROVE'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : decision.ai_decision === 'DECLINE'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                            : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        }`}>
                          {decision.ai_decision}
                        </span>
                        {decision.llm_recommendation && (
                          <>
                            <span className="text-xs text-gray-400 ml-2">AI Recommendation:</span>
                            <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                              decision.llm_recommendation === 'APPROVE'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              {decision.llm_recommendation}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* E-Transfer Hidden Lender Detection */}
                {etransfer && (
                  <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-amber-300 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Hidden Lender Detection (E-Transfers)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-white">{etransfer.total_etransfers}</div>
                        <div className="text-xs text-gray-400">Total E-Transfers</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-orange-400">{etransfer.etransfers_out}</div>
                        <div className="text-xs text-gray-400">Outgoing</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-400">{etransfer.etransfers_in}</div>
                        <div className="text-xs text-gray-400">Incoming</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className={`text-2xl font-bold ${etransfer.hidden_lender_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {etransfer.hidden_lender_count}
                        </div>
                        <div className="text-xs text-gray-400">Hidden Lenders</div>
                      </div>
                    </div>

                    {etransfer.suspicious_recipients && etransfer.suspicious_recipients.length > 0 && (
                      <div className="bg-gray-900/50 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-800/50">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-400 font-medium">Recipient</th>
                              <th className="px-3 py-2 text-right text-gray-400 font-medium">Sent</th>
                              <th className="px-3 py-2 text-right text-gray-400 font-medium">Received</th>
                              <th className="px-3 py-2 text-right text-gray-400 font-medium">Round %</th>
                              <th className="px-3 py-2 text-left text-gray-400 font-medium">Classification</th>
                            </tr>
                          </thead>
                          <tbody>
                            {etransfer.suspicious_recipients.map((r, i) => (
                              <tr key={i} className="border-t border-gray-700/50">
                                <td className="px-3 py-2 text-white font-medium">{r.name}</td>
                                <td className="px-3 py-2 text-right text-orange-400">${r.total_sent.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right text-green-400">${r.total_received.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right text-gray-300">{r.pct_round}%</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    r.classification === 'confirmed_lender' ? 'bg-red-500/20 text-red-400' :
                                    r.classification === 'possible_informal' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {r.classification.replace('_', ' ')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="px-3 py-2 bg-red-500/10 border-t border-red-500/30 text-sm">
                          <span className="text-red-400 font-medium">Total to Hidden Lenders: ${etransfer.hidden_lender_total.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Payday Drain Analysis */}
                {paydayDrain && (
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-purple-300 mb-3 flex items-center gap-2">
                      <ArrowDownRight className="w-4 h-4" />
                      Payday Drain Analysis
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-white">{paydayDrain.paydays_analyzed}</div>
                        <div className="text-xs text-gray-400">Paydays Analyzed</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className={`text-2xl font-bold ${paydayDrain.avg_drain_pct > 80 ? 'text-red-400' : paydayDrain.avg_drain_pct > 60 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {paydayDrain.avg_drain_pct.toFixed(0)}%
                        </div>
                        <div className="text-xs text-gray-400">Avg Drain</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className={`text-2xl font-bold ${paydayDrain.over_100pct_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {paydayDrain.over_100pct_count}
                        </div>
                        <div className="text-xs text-gray-400">&gt;100% Drain Days</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <div className={`text-2xl font-bold ${paydayDrain.debt_spiral_score > 50 ? 'text-red-400' : 'text-green-400'}`}>
                          {paydayDrain.debt_spiral_score.toFixed(0)}
                        </div>
                        <div className="text-xs text-gray-400">Spiral Score</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          paydayDrain.pattern.includes('SEVERE') ? 'bg-red-500/30 text-red-400' :
                          paydayDrain.pattern.includes('MODERATE') ? 'bg-yellow-500/30 text-yellow-400' :
                          'bg-green-500/30 text-green-400'
                        }`}>
                          {paydayDrain.pattern.replace(/_/g, ' ')}
                        </span>
                        <div className="text-xs text-gray-400 mt-1">Pattern</div>
                      </div>
                    </div>

                    {paydayDrain.worst_events && paydayDrain.worst_events.length > 0 && (
                      <div className="bg-gray-900/50 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-gray-800/50 text-xs text-gray-400 font-medium">Worst Payday Events</div>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-800/30">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-400 font-medium">Date</th>
                              <th className="px-3 py-2 text-right text-gray-400 font-medium">Income</th>
                              <th className="px-3 py-2 text-right text-gray-400 font-medium">Same-Day Out</th>
                              <th className="px-3 py-2 text-right text-gray-400 font-medium">Drain %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paydayDrain.worst_events.map((e, i) => (
                              <tr key={i} className="border-t border-gray-700/50">
                                <td className="px-3 py-2 text-white">{new Date(e.date).toLocaleDateString()}</td>
                                <td className="px-3 py-2 text-right text-green-400">${e.income.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right text-orange-400">${e.same_day_out.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right">
                                  <span className={`font-bold ${e.drain_pct > 100 ? 'text-red-400' : e.drain_pct > 80 ? 'text-yellow-400' : 'text-green-400'}`}>
                                    {e.drain_pct.toFixed(0)}%
                                    {e.drain_pct > 100 && ' 🚨'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Detailed Analysis - Full Report (Behind Paywall) */}
                {(decision.senior_assessment?.full_text || decision.ai_full_analysis || decision.llm_reasoning) && (
                  <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-5 mb-4">
                    <h4 className="text-sm font-semibold text-blue-300 mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      AI Detailed Risk Analysis
                      <span className="ml-auto text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 rounded">Premium</span>
                    </h4>
                    <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap bg-gray-900/50 rounded-lg p-4 border border-gray-700/30 max-h-96 overflow-y-auto">
                      {decision.senior_assessment?.full_text || decision.ai_full_analysis || decision.llm_reasoning}
                    </div>
                    {decision.senior_assessment?.key_concerns && decision.senior_assessment.key_concerns.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-red-400 mb-2">Key Concerns:</div>
                        <div className="flex flex-wrap gap-2">
                          {decision.senior_assessment.key_concerns.map((flag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30">
                              {flag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Deep Analysis Button */}
                <div className="mt-6 border-t border-gray-700/50 pt-6">
                  <button
                    onClick={() => handleDeepAnalysis(decision)}
                    disabled={deepAnalysisRunning}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                  >
                    <Activity className={`w-5 h-5 ${deepAnalysisRunning ? 'animate-spin' : ''}`} />
                    <span>
                      {deepAnalysisRunning
                        ? `Running Deep Analysis... ${Math.floor(deepAnalysisTimer)}s`
                        : 'Run Deep Analysis (AI Audit)'}
                    </span>
                  </button>

                  {/* Deep Analysis Results */}
                  {deepAnalysisResult && (
                    <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-6 mt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="w-5 h-5 text-purple-400" />
                        <h3 className="text-lg font-bold text-white">Deep Analysis Findings</h3>
                      </div>
                      <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap bg-gray-900/50 rounded-lg p-4 border border-gray-700/30 overflow-y-auto max-h-96">
                        {deepAnalysisResult}
                      </div>
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <div className="mt-4 text-center text-xs text-gray-500">
                  Generated by Mothership IBV Pipeline • {new Date(decision.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Categorization Modal */}
      {categorizationModalOpen && (() => {
        console.log('RENDERING MODAL - selectedPattern:', selectedPattern);
        return (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn"
          onClick={() => setCategorizationModalOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-blue-500/30 rounded-xl max-w-2xl w-full shadow-2xl shadow-blue-500/20 animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/10 border-b border-blue-500/30 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🏷️</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Categorize Transaction</h3>
                    <p className="text-xs text-gray-400 truncate max-w-md">{selectedPattern}</p>
                  </div>
                </div>
                <button
                  onClick={() => setCategorizationModalOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Badge Grid */}
            <div className="p-6">
              <p className="text-sm text-gray-400 mb-4">
                Choose a category. This will apply to all matching transactions globally and train the AI system.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {BADGE_OPTIONS.map((badge) => (
                  <button
                    key={badge.type}
                    onClick={() => handleSaveCategorization(badge)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all hover:scale-105 ${badge.color}`}
                  >
                    <span className="text-3xl">{badge.emoji}</span>
                    <span className="text-xs font-semibold text-white">{badge.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Raw Decision Data Modal */}
      {rawDataModalOpen && selectedRawDecision && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setRawDataModalOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-500/30 rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl shadow-gray-500/20 animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-500/20 to-gray-600/10 border-b border-gray-500/30 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">📋</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">📤 Raw Out - Decision Output</h3>
                    <p className="text-xs text-gray-400">{selectedRawDecision.customer_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setRawDataModalOpen(false)}
                  className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <pre className="text-xs text-gray-300 bg-gray-900/50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap break-words">
                {JSON.stringify(selectedRawDecision, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Raw IBV Input Modal */}
      {rawIbvModalOpen && selectedRawIbvDecision && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setRawIbvModalOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-blue-500/30 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl shadow-blue-500/20 animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/10 border-b border-blue-500/30 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">📥</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">📥 Raw In - IBV Input</h3>
                    <p className="text-xs text-gray-400">{selectedRawIbvDecision.customer_name} - Original Inverite data</p>
                  </div>
                </div>
                <button
                  onClick={() => setRawIbvModalOpen(false)}
                  className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {selectedRawIbvDecision.raw_ibv_data ? (
                <>
                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-white">
                        {selectedRawIbvDecision.raw_ibv_data.accounts?.length || 0}
                      </div>
                      <div className="text-xs text-gray-400">Accounts</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-white">
                        {selectedRawIbvDecision.raw_ibv_data.accounts?.[0]?.transactions?.length || 0}
                      </div>
                      <div className="text-xs text-gray-400">Transactions</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-green-400">
                        ${(selectedRawIbvDecision.raw_ibv_data.accounts?.[0]?.current_balance || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-400">Current Balance</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-blue-400">
                        {selectedRawIbvDecision.raw_ibv_data.accounts?.[0]?.bank || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400">Bank</div>
                    </div>
                  </div>
                  {/* Full JSON */}
                  <pre className="text-xs text-gray-300 bg-gray-900/50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap break-words max-h-[60vh] overflow-y-auto">
                    {JSON.stringify(selectedRawIbvDecision.raw_ibv_data, null, 2)}
                  </pre>
                </>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No raw IBV data available for this decision
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unified Raw Data Modal (Input + Output) */}
      {unifiedRawDataModalOpen && selectedUnifiedRawDecision && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setUnifiedRawDataModalOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-blue-500/30 rounded-xl max-w-[95vw] w-full max-h-[90vh] overflow-hidden shadow-2xl shadow-blue-500/20 animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-orange-500/20 border-b border-blue-500/30 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Raw Data - Input & Output</h3>
                    <p className="text-xs text-gray-400">{selectedUnifiedRawDecision.customer_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setUnifiedRawDataModalOpen(false)}
                  className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Content - 2 Column Layout */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Raw Input */}
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-sm">📥</span>
                      </div>
                      <h4 className="text-sm font-bold text-blue-400">Raw Input - IBV Data</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(selectedUnifiedRawDecision.raw_ibv_data, null, 2));
                        }}
                        className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-xs text-green-400 hover:text-green-300 transition-colors flex items-center gap-2"
                        title="Copy to clipboard"
                      >
                        <span>📋</span>
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          const blob = new Blob([JSON.stringify(selectedUnifiedRawDecision.raw_ibv_data, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `input-${selectedUnifiedRawDecision.id}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-2"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                    </div>
                  </div>
                  {selectedUnifiedRawDecision.raw_ibv_data ? (
                    <pre className="text-xs text-gray-300 bg-gray-900/50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap break-words flex-1 overflow-y-auto max-h-[60vh]">
                      {JSON.stringify(selectedUnifiedRawDecision.raw_ibv_data, null, 2)}
                    </pre>
                  ) : (
                    <div className="text-center text-gray-500 py-8 bg-gray-900/50 rounded-lg">
                      No raw IBV data available
                    </div>
                  )}
                </div>

                {/* Right Column - Raw Output */}
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-sm">📤</span>
                      </div>
                      <h4 className="text-sm font-bold text-orange-400">Raw Output - Webhook Response</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const webhookResponse = {
                            success: true,
                            decision_id: selectedUnifiedRawDecision.id,
                            customer_id: selectedUnifiedRawDecision.raw_ibv_data?._meta?.customer_id,
                            reference_id: selectedUnifiedRawDecision.raw_ibv_data?.ibv_data?.request,
                            decision: selectedUnifiedRawDecision.decision,
                            approved: selectedUnifiedRawDecision.approved,
                            confidence: selectedUnifiedRawDecision.confidence_score,
                            risk_level: selectedUnifiedRawDecision.risk_level,
                            reasoning: selectedUnifiedRawDecision.decision_reasoning,
                            nsf_count_30d: selectedUnifiedRawDecision.nsf_count_30d,
                            overdraft_count_30d: selectedUnifiedRawDecision.overdraft_count_30d,
                            fee_analysis: selectedUnifiedRawDecision.fee_analysis,
                            income_analysis: selectedUnifiedRawDecision.income_analysis,
                            lender_analysis: selectedUnifiedRawDecision.lender_analysis,
                            spending_analysis: selectedUnifiedRawDecision.spending_analysis,
                            forensic_analysis: selectedUnifiedRawDecision.forensic_analysis,
                            uncertainty_report: selectedUnifiedRawDecision.uncertainty_report,
                            uncertainty_resolution: selectedUnifiedRawDecision.uncertainty_resolution,
                            processing_time_ms: selectedUnifiedRawDecision.processing_time_ms,
                            stage_timings: selectedUnifiedRawDecision.stage_timings
                          };
                          navigator.clipboard.writeText(JSON.stringify(webhookResponse, null, 2));
                        }}
                        className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-xs text-green-400 hover:text-green-300 transition-colors flex items-center gap-2"
                        title="Copy to clipboard"
                      >
                        <span>📋</span>
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          const webhookResponse = {
                            success: true,
                            decision_id: selectedUnifiedRawDecision.id,
                            customer_id: selectedUnifiedRawDecision.raw_ibv_data?._meta?.customer_id,
                            reference_id: selectedUnifiedRawDecision.raw_ibv_data?.ibv_data?.request,
                            decision: selectedUnifiedRawDecision.decision,
                            approved: selectedUnifiedRawDecision.approved,
                            confidence: selectedUnifiedRawDecision.confidence_score,
                            risk_level: selectedUnifiedRawDecision.risk_level,
                            reasoning: selectedUnifiedRawDecision.decision_reasoning,
                            nsf_count_30d: selectedUnifiedRawDecision.nsf_count_30d,
                            overdraft_count_30d: selectedUnifiedRawDecision.overdraft_count_30d,
                            fee_analysis: selectedUnifiedRawDecision.fee_analysis,
                            income_analysis: selectedUnifiedRawDecision.income_analysis,
                            lender_analysis: selectedUnifiedRawDecision.lender_analysis,
                            spending_analysis: selectedUnifiedRawDecision.spending_analysis,
                            forensic_analysis: selectedUnifiedRawDecision.forensic_analysis,
                            uncertainty_report: selectedUnifiedRawDecision.uncertainty_report,
                            uncertainty_resolution: selectedUnifiedRawDecision.uncertainty_resolution,
                            processing_time_ms: selectedUnifiedRawDecision.processing_time_ms,
                            stage_timings: selectedUnifiedRawDecision.stage_timings
                          };
                          const blob = new Blob([JSON.stringify(webhookResponse, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `output-${selectedUnifiedRawDecision.id}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-lg text-xs text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-2"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                    </div>
                  </div>
                  <pre className="text-xs text-gray-300 bg-gray-900/50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap break-words flex-1 overflow-y-auto max-h-[60vh]">
                    {JSON.stringify({
                      success: true,
                      decision_id: selectedUnifiedRawDecision.id,
                      customer_id: selectedUnifiedRawDecision.raw_ibv_data?._meta?.customer_id,
                      reference_id: selectedUnifiedRawDecision.raw_ibv_data?.ibv_data?.request,
                      decision: selectedUnifiedRawDecision.decision,
                      approved: selectedUnifiedRawDecision.approved,
                      confidence: selectedUnifiedRawDecision.confidence_score,
                      risk_level: selectedUnifiedRawDecision.risk_level,
                      reasoning: selectedUnifiedRawDecision.decision_reasoning,
                      nsf_count_30d: selectedUnifiedRawDecision.nsf_count_30d,
                      overdraft_count_30d: selectedUnifiedRawDecision.overdraft_count_30d,
                      fee_analysis: selectedUnifiedRawDecision.fee_analysis,
                      income_analysis: selectedUnifiedRawDecision.income_analysis,
                      lender_analysis: selectedUnifiedRawDecision.lender_analysis,
                      spending_analysis: selectedUnifiedRawDecision.spending_analysis,
                      forensic_analysis: selectedUnifiedRawDecision.forensic_analysis,
                      uncertainty_report: selectedUnifiedRawDecision.uncertainty_report,
                      uncertainty_resolution: selectedUnifiedRawDecision.uncertainty_resolution,
                      processing_time_ms: selectedUnifiedRawDecision.processing_time_ms,
                      stage_timings: selectedUnifiedRawDecision.stage_timings
                    }, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {settingsModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setSettingsModalOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-blue-500/30 rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl shadow-blue-500/20 animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-b border-blue-500/30 p-4 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">LLM Settings</h3>
                    <p className="text-xs text-gray-400">Configure AI model and monitor health</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettingsModalOpen(false)}
                  className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden space-y-6">
              {/* LLM Health Status */}
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${currentModel ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    LLM Health Status
                  </h4>
                  <span className="text-xs text-gray-400">Ollama @ localhost:11434</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Status:</span>
                    <span className={`ml-2 font-semibold ${currentModel ? 'text-green-400' : 'text-red-400'}`}>
                      {currentModel ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Active Model:</span>
                    <span className="ml-2 font-semibold text-blue-400">{currentModel || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Model Switcher */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Select AI Model
                </label>
                <select
                  value={currentModel || ''}
                  onChange={async (e) => {
                    const newModel = e.target.value;
                    setModelSwitching(true);
                    try {
                      const apiBase = window.location.origin;
                      const res = await fetch(`${apiBase}/api/ibv/switch-model`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-API-Key': 'synapse-dev'
                        },
                        body: JSON.stringify({ model: newModel })
                      });

                      if (res.ok) {
                        const data = await res.json();
                        setCurrentModel(newModel);
                        console.log(`[LLM] Successfully switched to model: ${newModel}`);

                        // Show success notification
                        addNotification({
                          type: 'system',
                          title: 'Model Switched',
                          message: `Successfully switched to ${newModel}`
                        });
                      } else {
                        const errorData = await res.json();
                        console.error('[LLM] Failed to switch model:', errorData);

                        // Show error notification
                        addNotification({
                          type: 'system',
                          title: 'Model Switch Failed',
                          message: errorData.error || 'Failed to switch model'
                        });
                      }
                    } catch (error) {
                      console.error('[LLM] Error switching model:', error);

                      // Show error notification
                      addNotification({
                        type: 'system',
                        title: 'Model Switch Failed',
                        message: 'Network error while switching model'
                      });
                    } finally {
                      setModelSwitching(false);
                    }
                  }}
                  disabled={modelSwitching}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="llama3.1:8b">llama3.1:8b (Fast - 8B params)</option>
                  <option value="qwen2.5-coder:7b">qwen2.5-coder:7b (Coding-Focused - 7B params)</option>
                  <option value="qwen2.5:14b">qwen2.5:14b (Balanced - 14.8B params)</option>
                  <option value="qwen2.5:32b">qwen2.5:32b (Best Quality - 32.8B params)</option>
                  <option value="deepseek-r1:8b">deepseek-r1:8b (Reasoning - 8.2B params)</option>
                  <option value="deepseek-r1:32b">deepseek-r1:32b (Premium Reasoning - 32B params)</option>
                </select>
                <p className="mt-2 text-xs text-gray-400">
                  {modelSwitching ? (
                    <span className="text-yellow-400">Switching model and preloading...</span>
                  ) : (
                    'Changes take effect immediately for new deep analysis requests'
                  )}
                </p>
              </div>

              {/* Specification Documents */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700">
                  <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-400" />
                    <span>Specification Documents</span>
                    <span className="text-sm text-gray-500 font-normal ml-2">({specs.length})</span>
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">Download project specs and documentation</p>
                </div>
                <div className="p-6">
                  {specsLoading ? (
                    <div className="text-center text-gray-400 py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
                      <p className="mt-2 text-sm">Loading specs...</p>
                    </div>
                  ) : specs.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No specification documents available</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {specs.map((spec) => (
                        <div
                          key={spec.filename}
                          className="flex items-center justify-between p-4 bg-gray-900/50 border border-gray-700 rounded-lg hover:border-cyan-500 transition-all group"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                                <FileText className="w-5 h-5 text-cyan-400" />
                              </div>
                              <div>
                                <h5 className="text-white font-medium capitalize">{spec.name}</h5>
                                <div className="flex gap-3 text-xs text-gray-400 mt-1">
                                  <span>{formatFileSize(spec.size)}</span>
                                  <span>•</span>
                                  <span>Modified {formatDate(spec.modified)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => downloadSpec(spec.filename)}
                            className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/50 hover:border-cyan-400 text-cyan-400 rounded-lg font-medium transition-all flex items-center gap-2 opacity-0 group-hover:opacity-100"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setSettingsModalOpen(false)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Review Modal - Enhanced Design */}
      {reviewModalOpen && selectedReviewDecision && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700/50 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header with Gradient */}
            <div className="bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10 border-b border-gray-700/50 p-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertCircle className="w-8 h-8 text-yellow-400" />
                    <h2 className="text-3xl font-bold text-white">{selectedReviewDecision.customer_name}</h2>
                  </div>
                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    <span className="text-gray-400">
                      Ref: <span className="font-mono text-blue-400">{selectedReviewDecision.reference_id}</span>
                    </span>
                    {selectedReviewDecision.raw_ibv_data?.accounts?.[0]?.bank && (
                      <span className="text-gray-400">
                        Bank: <span className="font-semibold text-cyan-400">{selectedReviewDecision.raw_ibv_data.accounts[0].bank}</span>
                      </span>
                    )}
                    {selectedReviewDecision.raw_ibv_data?.accounts?.[0]?.institution && (
                      <span className="text-gray-400">
                        Inst: <span className="font-mono text-purple-400">{selectedReviewDecision.raw_ibv_data.accounts[0].institution}</span>
                      </span>
                    )}
                    <span className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 rounded-full text-xs font-semibold">
                      ⚠ REQUIRES REVIEW
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setReviewModalOpen(false)}
                  className="text-gray-500 hover:text-gray-300 hover:bg-gray-800/50 rounded-lg p-2 transition-all"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-280px)] p-8 space-y-6">
              {/* Key Metrics - Large Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-5 hover:border-blue-500/40 transition-all">
                  <DollarSign className="w-6 h-6 text-blue-400 mb-2" />
                  <div className="text-xs text-gray-400 mb-1">Monthly Income</div>
                  <div className="text-2xl font-bold text-white">
                    ${selectedReviewDecision.monthly_income_avg ? formatMoney(selectedReviewDecision.monthly_income_avg) : 'N/A'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 rounded-xl p-5 hover:border-red-500/40 transition-all">
                  <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
                  <div className="text-xs text-gray-400 mb-1">NSF Count</div>
                  <div className="text-2xl font-bold text-white">
                    {selectedReviewDecision.nsf_count || 0}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-xl p-5 hover:border-orange-500/40 transition-all">
                  <TrendingUp className="w-6 h-6 text-orange-400 mb-2" />
                  <div className="text-xs text-gray-400 mb-1">Total Fees</div>
                  <div className="text-2xl font-bold text-white">
                    ${selectedReviewDecision.total_fees ? formatMoney(selectedReviewDecision.total_fees) : 'N/A'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-5 hover:border-purple-500/40 transition-all">
                  <Users className="w-6 h-6 text-purple-400 mb-2" />
                  <div className="text-xs text-gray-400 mb-1">Active Lenders</div>
                  <div className="text-2xl font-bold text-white">
                    {selectedReviewDecision.lender_count || 0}
                  </div>
                </div>
              </div>

              {/* Side by Side Stats Comparison */}
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-bold text-white">Analysis Comparison</h3>
                </div>

                {/* Stats Grid */}
                <div className="space-y-3">
                  {/* Header Row */}
                  <div className="grid grid-cols-3 gap-4 pb-3 border-b border-gray-700/50">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Metric</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold text-center flex items-center justify-center gap-2">
                      <Zap className="w-4 h-4 text-blue-400" />
                      Rust Engine
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold text-center flex items-center justify-center gap-2">
                      <Target className="w-4 h-4 text-purple-400" />
                      AI Analysis
                    </div>
                  </div>

                  {/* NSF Count */}
                  <div className="grid grid-cols-3 gap-4 items-center py-2 hover:bg-gray-800/30 rounded-lg px-2 transition-colors">
                    <div className="text-sm text-gray-300 font-medium">NSF Count (30d)</div>
                    <div className={`text-center text-lg font-bold ${
                      (selectedReviewDecision.nsf_count ?? 0) > 5 ? 'text-red-400' : 'text-green-400'
                    }`}>
                      {selectedReviewDecision.nsf_count ?? 0}
                    </div>
                    <div className="text-center text-sm text-gray-400">
                      {selectedReviewDecision.ai_summary?.match(/(\d+)\s*NSF/i)?.[1] || '-'}
                    </div>
                  </div>

                  {/* Total Fees */}
                  <div className="grid grid-cols-3 gap-4 items-center py-2 hover:bg-gray-800/30 rounded-lg px-2 transition-colors">
                    <div className="text-sm text-gray-300 font-medium">Total Fees (30d)</div>
                    <div className={`text-center text-lg font-bold ${
                      (selectedReviewDecision.total_fees ?? 0) > 300 ? 'text-red-400' : 'text-green-400'
                    }`}>
                      ${formatMoney(selectedReviewDecision.total_fees ?? 0)}
                    </div>
                    <div className="text-center text-sm text-gray-400">
                      ${selectedReviewDecision.ai_summary?.match(/\$(\d+)\s*(?:in\s*)?fees/i)?.[1] || '-'}
                    </div>
                  </div>

                  {/* Monthly Income */}
                  <div className="grid grid-cols-3 gap-4 items-center py-2 hover:bg-gray-800/30 rounded-lg px-2 transition-colors">
                    <div className="text-sm text-gray-300 font-medium">Monthly Income</div>
                    <div className="text-center text-lg font-bold text-blue-400">
                      ${formatMoney(selectedReviewDecision.monthly_income_avg ?? 0)}
                    </div>
                    <div className="text-center text-sm text-gray-400">
                      ${selectedReviewDecision.ai_summary?.match(/income[:\s]+\$?(\d+)/i)?.[1] || '-'}
                    </div>
                  </div>

                  {/* Active Lenders */}
                  <div className="grid grid-cols-3 gap-4 items-center py-2 hover:bg-gray-800/30 rounded-lg px-2 transition-colors">
                    <div className="text-sm text-gray-300 font-medium">Active Lenders</div>
                    <div className={`text-center text-lg font-bold ${
                      (selectedReviewDecision.lender_count ?? 0) > 3 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {selectedReviewDecision.lender_count ?? 0}
                    </div>
                    <div className="text-center text-sm text-gray-400">
                      {selectedReviewDecision.ai_summary?.match(/(\d+)\s*lenders?/i)?.[1] || '-'}
                    </div>
                  </div>

                  {/* Loan Load */}
                  {selectedReviewDecision.loan_load_pct != null && (
                    <div className="grid grid-cols-3 gap-4 items-center py-2 hover:bg-gray-800/30 rounded-lg px-2 transition-colors">
                      <div className="text-sm text-gray-300 font-medium">Loan Load %</div>
                      <div className={`text-center text-lg font-bold ${
                        (selectedReviewDecision.loan_load_pct ?? 0) > 30 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        {(selectedReviewDecision.loan_load_pct ?? 0).toFixed(1)}%
                      </div>
                      <div className="text-center text-sm text-gray-400">-</div>
                    </div>
                  )}
                </div>

                {/* Decision Row */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-700/50">
                  <div className="text-sm text-gray-300 font-bold">Final Decision</div>
                  <div className={`text-center py-2 px-3 rounded-lg font-bold text-sm border ${
                    selectedReviewDecision.decision === 'APPROVED'
                      ? 'bg-green-500/20 border-green-500/40 text-green-400'
                      : selectedReviewDecision.decision === 'REVIEW'
                      ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400'
                      : 'bg-red-500/20 border-red-500/40 text-red-400'
                  }`}>
                    {selectedReviewDecision.decision}
                  </div>
                  <div className={`text-center py-2 px-3 rounded-lg font-bold text-sm border ${
                    selectedReviewDecision.ai_decision === 'APPROVE'
                      ? 'bg-green-500/20 border-green-500/40 text-green-400'
                      : selectedReviewDecision.ai_decision === 'REVIEW'
                      ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400'
                      : 'bg-red-500/20 border-red-500/40 text-red-400'
                  }`}>
                    {selectedReviewDecision.ai_decision || 'PENDING'}
                  </div>
                </div>

                {/* AI Reasoning */}
                {selectedReviewDecision.llm_recommendation && (
                  <div className="mt-6 pt-4 border-t border-gray-700/50">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">AI Reasoning</div>
                    <div className="text-sm text-gray-300 leading-relaxed bg-gray-900/50 rounded-lg p-4 border border-gray-700/30">
                      {selectedReviewDecision.llm_recommendation}
                    </div>
                  </div>
                )}
              </div>

              {/* Criteria Checklist */}
              {selectedReviewDecision.criteria && (
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-bold text-white">Criteria Checklist</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedReviewDecision.criteria).map(([key, value]) => (
                      <div key={key} className={`flex items-center gap-3 p-3 rounded-lg border ${
                        value
                          ? 'bg-green-500/10 border-green-500/30'
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          value ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {value ? '✓' : '✗'}
                        </div>
                        <span className="text-sm text-gray-200 font-medium">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Deep Analysis Section */}
            {deepAnalysisResult && (
              <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-6 mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-bold text-white">Deep Analysis Findings</h3>
                </div>
                <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap bg-gray-900/50 rounded-lg p-4 border border-gray-700/30 overflow-y-auto max-h-96">
                  {deepAnalysisResult}
                </div>
              </div>
            )}

            {/* Action Buttons - Sticky Footer */}
            <div className="sticky bottom-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-t border-gray-700/50 p-6">
              {/* Deep Analysis Button */}
              <button
                onClick={() => handleDeepAnalysis()}
                disabled={deepAnalysisRunning}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 mb-4"
              >
                <Activity className={`w-5 h-5 ${deepAnalysisRunning ? 'animate-spin' : ''}`} />
                <span>
                  {deepAnalysisRunning
                    ? `Running Deep Analysis... ${Math.floor(deepAnalysisTimer)}s`
                    : 'Run Deep Analysis (AI Audit)'}
                </span>
              </button>

              {/* Applicant Analytics Button */}
              <button
                onClick={() => {
                  setSelectedApplicantAnalytics(selectedReviewDecision);
                  setApplicantAnalyticsModalOpen(true);
                }}
                className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 mb-4"
              >
                <BarChart3 className="w-5 h-5" />
                <span>View Applicant Analytics</span>
              </button>

              {/* View All Transactions Button */}
              <button
                onClick={() => {
                  setSelectedTransactionDecision(selectedReviewDecision);
                  setTransactionModalOpen(true);
                }}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 mb-4"
              >
                <FileText className="w-5 h-5" />
                <span>View All Transactions (365 Days)</span>
              </button>

              <div className="flex gap-6 justify-center">
                <button
                  onClick={() => handleApproveDeclineReview(true)}
                  disabled={reviewUpdating}
                  title="Approve Application"
                  className="w-24 h-24 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-all duration-200 flex items-center justify-center shadow-lg shadow-green-500/20 hover:shadow-green-500/40 hover:scale-110 transform"
                >
                  {reviewUpdating ? (
                    <div className="animate-spin text-4xl">⚙️</div>
                  ) : (
                    <CheckCircle className="w-14 h-14" />
                  )}
                </button>
                <button
                  onClick={() => handleApproveDeclineReview(false)}
                  disabled={reviewUpdating}
                  title="Decline Application"
                  className="w-24 h-24 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-all duration-200 flex items-center justify-center shadow-lg shadow-red-500/20 hover:shadow-red-500/40 hover:scale-110 transform"
                >
                  {reviewUpdating ? (
                    <div className="animate-spin text-4xl">⚙️</div>
                  ) : (
                    <XCircle className="w-14 h-14" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History Modal (365 days) */}
      {transactionModalOpen && selectedTransactionDecision && (() => {
        const decision = selectedTransactionDecision;

        // Extract transactions from raw_ibv_data OR data_extraction (pre-tagged)
        interface Transaction {
          date: string;
          description: string;
          amount: number;
          type: 'CREDIT' | 'DEBIT';
          balance: number;
          category: string;
          flags: string[];
          tags?: string[];  // NEW: Pre-computed tags from Rust (gambling, nsf, lender, etc.)
          metadata?: {      // NEW: Boolean flags for quick filtering
            is_income?: boolean;
            is_gambling?: boolean;
            is_nsf?: boolean;
            is_lender_payment?: boolean;
            is_fee?: boolean;
            is_etransfer?: boolean;
            is_atm?: boolean;
            likely_payday?: boolean;
          };
          bank: string;
          accountIndex: number;
          payCycle: number;
          originalDate?: Date;
        }

        let transactions: Transaction[] = [];

        // Extract all pay dates from payschedules across all accounts
        const allPayDates: Date[] = [];
        decision.raw_ibv_data?.accounts?.forEach((account: any) => {
          if (account.payschedules) {
            account.payschedules.forEach((schedule: any) => {
              schedule.payments?.forEach((payment: any) => {
                if (payment.date) {
                  allPayDates.push(new Date(payment.date));
                }
              });
            });
          }
        });
        allPayDates.sort((a, b) => a.getTime() - b.getTime());

        // Function to determine which pay cycle a transaction belongs to
        const getPayCycle = (txnDate: Date): number => {
          if (allPayDates.length === 0) return 0;

          // Find the most recent payday before or on this transaction date
          let payCycleIndex = 0;
          for (let i = 0; i < allPayDates.length; i++) {
            if (allPayDates[i] <= txnDate) {
              payCycleIndex = i;
            } else {
              break;
            }
          }
          return payCycleIndex;
        };

        // PRIORITY 1: Use pre-tagged data_extraction if available (Stage 1 output)
        // This has all tags, flags, and metadata already computed by Rust
        const extractedAccounts = decision.data_extraction?.accounts;

        if (extractedAccounts && extractedAccounts.length > 0) {
          const now = new Date();
          const yearAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 365);

          // Use pre-tagged transactions from Rust data extractor
          transactions = extractedAccounts.flatMap((account: any, accountIndex: number) =>
            (account.transactions || []).map((txn: any) => {
              const txnDate = new Date(txn.date);

              return {
                date: txnDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
                description: txn.description || 'Unknown Transaction',
                amount: txn.amount || 0,
                type: txn.tx_type as 'CREDIT' | 'DEBIT',
                balance: txn.balance || 0,
                category: txn.category || 'uncategorized',
                flags: txn.flags || [],
                tags: txn.tags || [],  // NEW: Pre-computed tags from Rust
                metadata: txn.metadata || {},  // NEW: Boolean flags for quick checks
                bank: account.bank || 'Unknown Bank',
                accountIndex: accountIndex,
                payCycle: getPayCycle(txnDate),
                originalDate: txnDate
              };
            })
          )
            .filter((txn: Transaction) => txn.originalDate && !isNaN(txn.originalDate.getTime()) && txn.originalDate >= yearAgo)
            .sort((a: Transaction, b: Transaction) =>
              (b.originalDate?.getTime() || 0) - (a.originalDate?.getTime() || 0)
            );
        } else {
          // FALLBACK: Use raw_ibv_data if data_extraction not available (legacy)
          const accountsData = decision.raw_ibv_data?.accounts
            || decision.raw_ibv_data?.ibv_data?.accounts
            || decision.raw_ibv_data?.raw_ibv_data?.accounts
            || decision.raw_ibv_data?.raw_ibv_data?.ibv_data?.accounts;

          if (accountsData) {
            const now = new Date();
            const yearAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 365);

            // Collect transactions from ALL accounts (supporting multiple banks)
            transactions = accountsData.flatMap((account: any, accountIndex: number) =>
              (account.transactions || []).map((txn: any) => {
                // Parse date string (format: YYYY-MM-DD or similar)
                const txnDate = new Date(txn.date);

                // Determine amount and type from separate debit/credit fields
                const debitAmount = parseFloat(txn.debit || '0');
                const creditAmount = parseFloat(txn.credit || '0');
                const amount = debitAmount > 0 ? debitAmount : creditAmount;
                const type = debitAmount > 0 ? 'DEBIT' : 'CREDIT';

                return {
                  date: txnDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
                  description: txn.details || 'Unknown Transaction',
                  amount: amount,
                  type: type,
                  balance: parseFloat(txn.balance || '0'),
                  category: txn.category || 'uncategorized',
                  flags: txn.flags || [],
                  bank: account.bank || 'Unknown Bank',
                  accountIndex: accountIndex,
                  payCycle: getPayCycle(txnDate),
                  originalDate: txnDate
                };
              })
            )
              .filter((txn: Transaction) => txn.originalDate && !isNaN(txn.originalDate.getTime()) && txn.originalDate >= yearAgo)
              .sort((a: Transaction, b: Transaction) =>
                (b.originalDate?.getTime() || 0) - (a.originalDate?.getTime() || 0)
              );
          }
        }

        // Get unique categories and banks for filter dropdowns
        const uniqueCategories = Array.from(new Set(transactions.map(t => t.category))).sort();
        const uniqueBanks = Array.from(new Set(transactions.map(t => t.bank))).sort();

        // Bank color mapping (consistent colors for each bank)
        const bankColors: Record<string, string> = {};
        const colorPalette = [
          'border-cyan-500/30 bg-cyan-500/5',
          'border-purple-500/30 bg-purple-500/5',
          'border-green-500/30 bg-green-500/5',
          'border-orange-500/30 bg-orange-500/5',
          'border-pink-500/30 bg-pink-500/5',
        ];
        uniqueBanks.forEach((bank, idx) => {
          bankColors[bank] = colorPalette[idx % colorPalette.length];
        });

        // Apply filters and sorting
        let filteredTransactions = transactions.filter(txn => {
          const matchesType = txnFilterType === 'ALL' || txn.type === txnFilterType;
          const matchesCategory = txnFilterCategory === 'ALL' || txn.category === txnFilterCategory;
          const matchesSearch = txn.description.toLowerCase().includes(txnSearchTerm.toLowerCase());
          return matchesType && matchesCategory && matchesSearch;
        });

        // Sort transactions
        filteredTransactions.sort((a, b) => {
          let comparison = 0;
          if (txnSortField === 'date') {
            comparison = (a.originalDate?.getTime() || 0) - (b.originalDate?.getTime() || 0);
          } else if (txnSortField === 'amount') {
            comparison = a.amount - b.amount;
          } else if (txnSortField === 'balance') {
            comparison = a.balance - b.balance;
          }
          return txnSortDirection === 'asc' ? comparison : -comparison;
        });

        // Calculate totals
        const totalCredits = filteredTransactions
          .filter(t => t.type === 'CREDIT')
          .reduce((sum, t) => sum + t.amount, 0);
        const totalDebits = filteredTransactions
          .filter(t => t.type === 'DEBIT')
          .reduce((sum, t) => sum + t.amount, 0);
        const netFlow = totalCredits - totalDebits;

        // Calculate paycheck-to-first-spend periods for header count
        const incomeKeywords = ['payroll', 'salary', 'wages', 'direct deposit', 'dd deposit', 'employment', 'employer'];
        const transferKeywords = ['transfer', 'tfr', 'trf', 'e-transfer', 'etransfer', 'interac'];
        let paycheckToSpendCount = 0;

        filteredTransactions.forEach((txn, i) => {
          const desc = txn.description.toLowerCase();
          const isPaycheck = txn.type === 'CREDIT' &&
            txn.amount > 500 &&
            incomeKeywords.some(kw => desc.includes(kw));

          if (isPaycheck) {
            // Check if there's a first non-transfer debit after this
            for (let j = i + 1; j < filteredTransactions.length; j++) {
              const nextTxn = filteredTransactions[j];
              const nextDesc = nextTxn.description.toLowerCase();
              const isTransfer = transferKeywords.some(kw => nextDesc.includes(kw));

              if (nextTxn.type === 'DEBIT' && !isTransfer) {
                paycheckToSpendCount++;
                break;
              }
            }
          }
        });

        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-1 md:p-4 animate-fadeIn"
            onClick={() => setTransactionModalOpen(false)}
          >
            <div
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-purple-500/30 rounded-xl w-full max-w-full md:max-w-6xl lg:max-w-7xl max-h-[98vh] md:max-h-[90vh] shadow-2xl shadow-purple-500/20 animate-slideUp flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-b border-purple-500/30 p-2 md:p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{decision.customer_name} - Transaction History</h3>
                      <p className="text-xs text-gray-400">
                        Last 365 days • {filteredTransactions.length} of {transactions.length} transactions
                        {paycheckToSpendCount > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-500/20 border border-yellow-500/30 text-yellow-400">
                            💰→💸 {paycheckToSpendCount} paycheck periods
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setTransactionModalOpen(false)}
                      className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>

              {/* Scrollable Content Wrapper */}
              <div className="flex-1 min-h-0 overflow-y-auto">

              {/* Stats Bar */}
              <div className="bg-gray-800/30 border-b border-gray-700/50 p-2 md:p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2.5">
                    <div className="text-xs text-green-400 mb-1">Total Credits</div>
                    <div className="text-base md:text-lg font-bold text-green-400">${formatMoney(totalCredits, 2)}</div>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2.5">
                    <div className="text-xs text-red-400 mb-1">Total Debits</div>
                    <div className="text-base md:text-lg font-bold text-red-400">${formatMoney(totalDebits, 2)}</div>
                  </div>
                  <div className={`border rounded-lg p-2.5 ${netFlow >= 0 ? 'bg-blue-500/10 border-blue-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
                    <div className={`text-xs mb-1 ${netFlow >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>Net Flow</div>
                    <div className={`text-base md:text-lg font-bold ${netFlow >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                      {netFlow >= 0 ? '+' : ''}${formatMoney(netFlow, 2)}
                    </div>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-2.5">
                    <div className="text-xs text-purple-400 mb-1">Avg Transaction</div>
                    <div className="text-base md:text-lg font-bold text-purple-400">
                      ${formatMoney((totalCredits + totalDebits) / filteredTransactions.length || 0, 2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Forensic Analysis Results */}
              {txnDeepAnalysisResult && (
                <div className="bg-orange-500/10 border-b border-orange-500/20 p-4 max-h-64 overflow-y-auto">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-orange-400 mb-2">🔍 FORENSIC ANALYSIS REPORT</h4>
                      <div className="text-xs text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                        {txnDeepAnalysisResult}
                      </div>
                    </div>
                    <button
                      onClick={() => setTxnDeepAnalysisResult(null)}
                      className="text-gray-500 hover:text-white transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* Filters and Search */}
              <div className="bg-gray-800/30 border-b border-gray-700/50 p-4">
                <div className="flex gap-3 items-center flex-wrap">
                  {/* Search */}
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={txnSearchTerm}
                      onChange={(e) => setTxnSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                    />
                  </div>

                  {/* Type Filter */}
                  <div className="flex gap-2">
                    {(['ALL', 'CREDIT', 'DEBIT'] as const).map(type => (
                      <button
                        key={type}
                        onClick={() => setTxnFilterType(type)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                          txnFilterType === type
                            ? type === 'CREDIT'
                              ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                              : type === 'DEBIT'
                              ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                              : 'bg-purple-500/20 border border-purple-500/50 text-purple-400'
                            : 'bg-gray-800/50 border border-gray-700 text-gray-400 hover:text-white'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  {/* Category Filter */}
                  <select
                    value={txnFilterCategory}
                    onChange={(e) => setTxnFilterCategory(e.target.value)}
                    className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white text-xs font-medium focus:outline-none focus:border-purple-500/50"
                  >
                    <option value="ALL">All Categories</option>
                    {uniqueCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Transaction Table */}
              <div className="overflow-x-auto -mx-2 md:mx-0">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-800/90 backdrop-blur-sm border-b border-gray-700">
                    <tr>
                      <th
                        onClick={() => {
                          if (txnSortField === 'date') {
                            setTxnSortDirection(txnSortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setTxnSortField('date');
                            setTxnSortDirection('desc');
                          }
                        }}
                        className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white transition-colors"
                      >
                        Date {txnSortField === 'date' && (txnSortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">
                        Description
                      </th>
                      <th
                        onClick={() => {
                          if (txnSortField === 'amount') {
                            setTxnSortDirection(txnSortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setTxnSortField('amount');
                            setTxnSortDirection('desc');
                          }
                        }}
                        className="px-2 md:px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white transition-colors"
                      >
                        Amount {txnSortField === 'amount' && (txnSortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="hidden md:table-cell px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">
                        Type
                      </th>
                      <th
                        onClick={() => {
                          if (txnSortField === 'balance') {
                            setTxnSortDirection(txnSortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setTxnSortField('balance');
                            setTxnSortDirection('desc');
                          }
                        }}
                        className="px-2 md:px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase cursor-pointer hover:text-white transition-colors"
                      >
                        Balance {txnSortField === 'balance' && (txnSortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No transactions found
                        </td>
                      </tr>
                    ) : (() => {
                      // Detect overdraft periods (balance < 0)
                      const overdraftPeriods: { start: number; end: number }[] = [];
                      let odStart: number | null = null;
                      filteredTransactions.forEach((txn, i) => {
                        if (txn.balance < 0) {
                          if (odStart === null) odStart = i;
                        } else {
                          if (odStart !== null) {
                            overdraftPeriods.push({ start: odStart, end: i - 1 });
                            odStart = null;
                          }
                        }
                      });
                      if (odStart !== null) {
                        overdraftPeriods.push({ start: odStart, end: filteredTransactions.length - 1 });
                      }

                      // **ADVANCED PAYCYCLE & BURN RATE DETECTION**
                      // Detect payday → $0 periods with days-to-burn gradient
                      const paycheckPeriods: { start: number; end: number; daysToBurn: number }[] = [];
                      const incomeKeywords = ['payroll', 'salary', 'wages', 'direct deposit', 'dd deposit', 'employment', 'employer'];
                      const transferKeywords = ['transfer', 'tfr', 'trf', 'e-transfer', 'etransfer', 'interac'];

                      filteredTransactions.forEach((txn, i) => {
                        const desc = txn.description.toLowerCase();
                        const isPaycheck = txn.type === 'CREDIT' &&
                          txn.amount > 500 &&
                          incomeKeywords.some(kw => desc.includes(kw));

                        if (isPaycheck) {
                          // Find when balance hits zero or next payday (whichever comes first)
                          let burnEndIdx = i;
                          let daysToBurn = 0;

                          for (let j = i + 1; j < filteredTransactions.length; j++) {
                            const nextTxn = filteredTransactions[j];
                            const nextDesc = nextTxn.description.toLowerCase();

                            // Stop at next payday
                            const isNextPayday = nextTxn.type === 'CREDIT' &&
                              nextTxn.amount > 500 &&
                              incomeKeywords.some(kw => nextDesc.includes(kw));

                            if (isNextPayday) {
                              burnEndIdx = j - 1;
                              break;
                            }

                            // Stop when balance hits zero or goes negative
                            if (nextTxn.balance <= 0) {
                              burnEndIdx = j;
                              break;
                            }

                            // If we reach end of transactions
                            if (j === filteredTransactions.length - 1) {
                              burnEndIdx = j;
                            }
                          }

                          // Calculate days to burn (using transaction dates)
                          const paydayDate = txn.originalDate;
                          const burnEndDate = filteredTransactions[burnEndIdx].originalDate;
                          if (paydayDate && burnEndDate) {
                            daysToBurn = Math.floor((burnEndDate.getTime() - paydayDate.getTime()) / (1000 * 60 * 60 * 24));
                          }

                          paycheckPeriods.push({ start: i, end: burnEndIdx, daysToBurn });
                        }
                      });

                      // Detect overdraft triggers (balance goes from positive to negative)
                      const overdraftTriggers: number[] = [];
                      filteredTransactions.forEach((txn, i) => {
                        if (i > 0) {
                          const prevBalance = filteredTransactions[i - 1].balance;
                          if (prevBalance >= 0 && txn.balance < 0) {
                            overdraftTriggers.push(i);
                          }
                        }
                      });

                      // Detect NSF fees and their return payments
                      const nsfReturns: Map<number, number> = new Map(); // Map of NSF index -> return index
                      const nsfFees = filteredTransactions
                        .map((txn, idx) => ({ txn, idx }))
                        .filter(({ txn }) => {
                          const desc = txn.description.toLowerCase();
                          return txn.type === 'DEBIT' &&
                            txn.amount >= 5 && txn.amount <= 100 &&
                            (desc.includes('nsf') || desc.includes('insufficient') ||
                             desc.includes('non-sufficient') || desc.includes('returned item'));
                        });

                      nsfFees.forEach(({ txn: nsfTxn, idx: nsfIdx }) => {
                        // Look for return/reversal within next 5 transactions (typically 1-3 days)
                        for (let i = nsfIdx + 1; i < Math.min(nsfIdx + 6, filteredTransactions.length); i++) {
                          const returnTxn = filteredTransactions[i];
                          const desc = returnTxn.description.toLowerCase();
                          const isReturn = returnTxn.type === 'CREDIT' &&
                            Math.abs(returnTxn.amount - nsfTxn.amount) < 0.01 &&
                            (desc.includes('return') || desc.includes('reversal') ||
                             desc.includes('refund') || desc.includes('credit adjustment'));

                          if (isReturn) {
                            nsfReturns.set(nsfIdx, i);
                            break;
                          }
                        }
                      });

                      // Detect balance zero crossings (positive ↔ negative)
                      const balanceCrossings: { index: number; direction: 'to_negative' | 'to_positive' }[] = [];
                      filteredTransactions.forEach((txn, i) => {
                        if (i > 0) {
                          const prevBalance = filteredTransactions[i - 1].balance;
                          if (prevBalance >= 0 && txn.balance < 0) {
                            balanceCrossings.push({ index: i, direction: 'to_negative' });
                          } else if (prevBalance < 0 && txn.balance >= 0) {
                            balanceCrossings.push({ index: i, direction: 'to_positive' });
                          }
                        }
                      });

                      // Track paycheck positions to detect when they go broke
                      const paycheckIndices: number[] = [];

                      filteredTransactions.forEach((txn, i) => {
                        const desc = txn.description.toLowerCase();
                        const endsWithPay = desc.endsWith(' pay') || desc.endsWith('pay');
                        const hasIncomeKeyword = incomeKeywords.some(kw => desc.includes(kw));
                        const isPaycheck = txn.type === 'CREDIT' && txn.amount > 500 && (hasIncomeKeyword || endsWithPay);

                        if (isPaycheck) {
                          paycheckIndices.push(i);
                        }
                      });


                      return filteredTransactions.map((txn, idx) => {
                        // **SIMPLE BACKGROUND**: Red when negative, pay cycle colors otherwise
                        const payCycleColors = [
                          'bg-blue-950/40',      // Cycle 0
                          'bg-purple-950/40',    // Cycle 1
                          'bg-indigo-950/40',    // Cycle 2
                          'bg-violet-950/40',    // Cycle 3
                          'bg-fuchsia-950/40',   // Cycle 4
                          'bg-pink-950/40',      // Cycle 5
                        ];

                        // Highlight negative balance in red
                        const payCycleBg = txn.balance < 0
                          ? 'bg-red-950/40'
                          : payCycleColors[txn.payCycle % payCycleColors.length];

                        // Bank color border/accent
                        const bankColor = uniqueBanks.length > 1 ? bankColors[txn.bank] || '' : '';

                        // Check if in overdraft period
                        const odPeriod = overdraftPeriods.find(p => idx >= p.start && idx <= p.end);
                        const isOdStart = odPeriod && idx === odPeriod.start;
                        const isOdEnd = odPeriod && idx === odPeriod.end;
                        const isInOd = !!odPeriod;

                        // Check if in paycheck period
                        const paycheckPeriod = paycheckPeriods.find(p => idx >= p.start && idx <= p.end);
                        const isPaycheckStart = paycheckPeriod && idx === paycheckPeriod.start;
                        const isPaycheckEnd = paycheckPeriod && idx === paycheckPeriod.end;
                        const isInPaycheck = !!paycheckPeriod;

                        // Check if this is an overdraft trigger
                        const isOdTrigger = overdraftTriggers.includes(idx);

                        // Check if this is an NSF fee with return
                        const returnIdx = nsfReturns.get(idx);
                        const hasReturn = returnIdx !== undefined;

                        // Check if this is a return for an NSF fee
                        const isReturnFor = Array.from(nsfReturns.entries()).find(([_, retIdx]) => retIdx === idx)?.[0];
                        const isNsfReturn = isReturnFor !== undefined;

                        // Check balance crossing
                        const crossing = balanceCrossings.find(c => c.index === idx);

                        // Build border classes
                        let borderClasses = '';
                        if (isInOd) {
                          borderClasses += ' border-l-2 border-r-2 border-red-500';
                          if (isOdStart) borderClasses += ' border-t-2 border-t-red-500';
                          if (isOdEnd) borderClasses += ' border-b-2 border-b-red-500';
                        }
                        if (isInPaycheck) {
                          borderClasses += ' border-l-2 border-r-2 border-yellow-500';
                          if (isPaycheckStart) borderClasses += ' border-t-2 border-t-yellow-500';
                          if (isPaycheckEnd) borderClasses += ' border-b-2 border-b-yellow-500';
                        }

                        // Check forensic flags for this transaction
                        const forensics = decision.forensic_analysis;
                        const badges: { text: string; color: string; emoji: string }[] = [];

                        // **CUSTOM RULES FIRST** - User-taught patterns (highest priority)
                        const desc = txn.description.toLowerCase();
                        let customRuleMatched = false;
                        for (const rule of customRules) {
                          if (desc.includes(rule.pattern)) {
                            badges.push({
                              text: rule.badge_type,
                              color: rule.badge_color,
                              emoji: rule.badge_emoji
                            });
                            customRuleMatched = true;
                            break; // Only apply first matching custom rule
                          }
                        }

                        // **NSF FEE & RETURN FLOW** - Show NSF fees and what caused them

                        // If this transaction is an NSF fee that got returned
                        if (hasReturn) {
                          badges.push({
                            text: 'NSF FEE',
                            color: 'bg-red-600/30 border-red-500/70 text-red-300',
                            emoji: '🚫'
                          });
                          badges.push({
                            text: `RETURNED ↓ line ${returnIdx! + 1}`,
                            color: 'bg-purple-600/30 border-purple-500/70 text-purple-300',
                            emoji: '↩️'
                          });
                        }
                        // If this transaction is a return/reversal of an NSF fee
                        else if (isNsfReturn) {
                          badges.push({
                            text: `REVERSAL ↑ from NSF line ${isReturnFor! + 1}`,
                            color: 'bg-purple-600/30 border-purple-500/70 text-purple-300',
                            emoji: '💸'
                          });
                        }
                        // Check for "RTN NSF" or similar return descriptions
                        else if (desc.includes('rtn nsf') || desc.includes('nsf rtn') || (desc.includes('rtn') && desc.includes('nsf'))) {
                          badges.push({
                            text: 'NSF RETURN',
                            color: 'bg-purple-600/30 border-purple-500/70 text-purple-300',
                            emoji: '💸'
                          });
                        }
                        // Regular NSF fee (no return found)
                        else if ((desc.includes('nsf') || desc.includes('insufficient') || desc.includes('non-sufficient')) && txn.type === 'DEBIT') {
                          badges.push({
                            text: 'NSF FEE',
                            color: 'bg-red-600/30 border-red-500/70 text-red-300',
                            emoji: '🚫'
                          });
                        }
                        // Generic fee detection
                        else if (desc.includes('fee') && txn.type === 'DEBIT') {
                          badges.push({
                            text: 'FEE',
                            color: 'bg-orange-600/30 border-orange-500/70 text-orange-300',
                            emoji: '💳'
                          });
                        }

                        // **MERCHANT-SPECIFIC BADGES** - Easy wins from description matching
                        else if (desc.includes('doordash') || desc.includes('door dash')) {
                          badges.push({ text: 'DOORDASH', color: 'bg-red-700/30 border-red-600/70 text-red-300', emoji: '🍔' });
                        }
                        else if (desc.includes('uber eats') || desc.includes('ubereats')) {
                          badges.push({ text: 'UBER EATS', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '🍱' });
                        }
                        else if (desc.includes('skip') && (desc.includes('dish') || desc.includes('the'))) {
                          badges.push({ text: 'SKIP', color: 'bg-orange-700/30 border-orange-600/70 text-orange-300', emoji: '🥡' });
                        }
                        else if (desc.includes('tim hortons') || desc.includes('tim horton')) {
                          badges.push({ text: 'TIM HORTONS', color: 'bg-red-700/30 border-red-600/70 text-red-300', emoji: '☕' });
                        }
                        else if (desc.includes('starbucks')) {
                          badges.push({ text: 'STARBUCKS', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '☕' });
                        }
                        else if (desc.includes('mcdonalds') || desc.includes("mcdonald's")) {
                          badges.push({ text: 'MCDONALDS', color: 'bg-yellow-700/30 border-yellow-600/70 text-yellow-300', emoji: '🍟' });
                        }
                        else if (desc.includes('walmart')) {
                          badges.push({ text: 'WALMART', color: 'bg-blue-700/30 border-blue-600/70 text-blue-300', emoji: '🛒' });
                        }
                        else if (desc.includes('amazon')) {
                          badges.push({ text: 'AMAZON', color: 'bg-orange-700/30 border-orange-600/70 text-orange-300', emoji: '📦' });
                        }
                        else if (desc.includes('netflix')) {
                          badges.push({ text: 'NETFLIX', color: 'bg-red-700/30 border-red-600/70 text-red-300', emoji: '🎬' });
                        }
                        else if (desc.includes('spotify')) {
                          badges.push({ text: 'SPOTIFY', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '🎵' });
                        }

                        // **SIMPLE BADGES** - Only show what matters

                        // **PAYCHECK** - Income deposits
                        const incomeKeywords = ['payroll', 'salary', 'wages', 'direct deposit', 'dd deposit', 'employment', 'employer'];
                        const hasIncomeKeyword = incomeKeywords.some(kw => desc.includes(kw));
                        const endsWithPay = desc.endsWith(' pay') || desc.endsWith('pay');

                        const isPaycheck = txn.type === 'CREDIT' &&
                          txn.amount > 500 &&
                          (hasIncomeKeyword || endsWithPay);

                        if (isPaycheck) {
                          badges.push({
                            text: 'PAYCHECK',
                            color: 'bg-green-700/30 border-green-600/70 text-green-300',
                            emoji: '💰'
                          });
                        }

                        // **INVERITE CATEGORY BADGES** - Easy wins, just check category strings
                        const category = txn.category?.toLowerCase() || '';

                        // Track if this is an NSF-related transaction (don't add LENDER badge to these)
                        const hasNsfBadge = badges.some(b =>
                          b.text.includes('NSF') || b.text.includes('REVERSAL') || b.text.includes('RETURN')
                        );

                        // Financial services - skip if already has NSF badge
                        const isLenderCategory = category.includes('loan') || category.includes('lending') || category.includes('credit_services');

                        if (isLenderCategory && !hasNsfBadge) {
                          // LOAN_IN (receiving loan) vs LOAN_OUT (paying back)
                          if (txn.type === 'CREDIT') {
                            badges.push({ text: 'LOAN_IN', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '💰' });
                          } else if (txn.type === 'DEBIT') {
                            badges.push({ text: 'LOAN_OUT', color: 'bg-red-700/30 border-red-600/70 text-red-300', emoji: '💸' });
                          }
                        }
                        // If NSF FEE + lender category, enhance the existing NSF badge
                        else if (isLenderCategory && badges.some(b => b.text === 'NSF FEE')) {
                          // Find and update the NSF FEE badge to show it's lender-related
                          const nsfBadgeIdx = badges.findIndex(b => b.text === 'NSF FEE');
                          if (nsfBadgeIdx >= 0) {
                            badges[nsfBadgeIdx] = {
                              text: 'LENDER NSF',
                              color: 'bg-red-700/30 border-red-600/70 text-red-300',
                              emoji: '🏦'
                            };
                          }
                        }

                        // Food & Drink
                        else if (category.includes('restaurant') || category.includes('food_and_drink')) {
                          badges.push({ text: 'FOOD', color: 'bg-orange-700/30 border-orange-600/70 text-orange-300', emoji: '🍔' });
                        }
                        else if (category.includes('groceries') || category.includes('supermarket')) {
                          badges.push({ text: 'GROCERIES', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '🛒' });
                        }
                        else if (category.includes('alcohol') || category.includes('bar') || category.includes('liquor')) {
                          badges.push({ text: 'ALCOHOL', color: 'bg-purple-700/30 border-purple-600/70 text-purple-300', emoji: '🍺' });
                        }

                        // Shopping & Retail
                        else if (category.includes('shopping') || category.includes('retail') || category.includes('merchandise')) {
                          badges.push({ text: 'SHOPPING', color: 'bg-pink-700/30 border-pink-600/70 text-pink-300', emoji: '🛍️' });
                        }
                        else if (category.includes('clothing') || category.includes('apparel')) {
                          badges.push({ text: 'CLOTHING', color: 'bg-indigo-700/30 border-indigo-600/70 text-indigo-300', emoji: '👕' });
                        }

                        // Transportation
                        else if (category.includes('gas') || category.includes('fuel') || category.includes('petrol')) {
                          badges.push({ text: 'GAS', color: 'bg-yellow-700/30 border-yellow-600/70 text-yellow-300', emoji: '⛽' });
                        }
                        else if (category.includes('parking') || category.includes('toll')) {
                          badges.push({ text: 'PARKING', color: 'bg-gray-700/30 border-gray-600/70 text-gray-300', emoji: '🅿️' });
                        }
                        else if (category.includes('taxi') || category.includes('uber') || category.includes('lyft') || category.includes('rideshare')) {
                          badges.push({ text: 'RIDESHARE', color: 'bg-blue-700/30 border-blue-600/70 text-blue-300', emoji: '🚗' });
                        }
                        else if (category.includes('transit') || category.includes('subway') || category.includes('bus')) {
                          badges.push({ text: 'TRANSIT', color: 'bg-cyan-700/30 border-cyan-600/70 text-cyan-300', emoji: '🚇' });
                        }

                        // Housing & Utilities
                        else if (category.includes('rent') || category.includes('lease')) {
                          badges.push({ text: 'RENT', color: 'bg-red-700/30 border-red-600/70 text-red-300', emoji: '🏠' });
                        }
                        else if (category.includes('utilities') || category.includes('electric') || category.includes('hydro') || category.includes('water')) {
                          badges.push({ text: 'UTILITIES', color: 'bg-yellow-700/30 border-yellow-600/70 text-yellow-300', emoji: '💡' });
                        }
                        else if (category.includes('internet') || category.includes('cable') || category.includes('telecom')) {
                          badges.push({ text: 'INTERNET', color: 'bg-blue-700/30 border-blue-600/70 text-blue-300', emoji: '📡' });
                        }
                        else if (category.includes('phone') || category.includes('mobile') || category.includes('wireless')) {
                          badges.push({ text: 'PHONE', color: 'bg-purple-700/30 border-purple-600/70 text-purple-300', emoji: '📱' });
                        }

                        // Entertainment & Subscriptions
                        else if (category.includes('subscription') || category.includes('streaming')) {
                          badges.push({ text: 'SUBSCRIPTION', color: 'bg-indigo-700/30 border-indigo-600/70 text-indigo-300', emoji: '📺' });
                        }

                        // Healthcare
                        else if (category.includes('health') || category.includes('medical') || category.includes('pharmacy') || category.includes('doctor')) {
                          badges.push({ text: 'HEALTHCARE', color: 'bg-red-700/30 border-red-600/70 text-red-300', emoji: '🏥' });
                        }

                        // Insurance
                        else if (category.includes('insurance')) {
                          badges.push({ text: 'INSURANCE', color: 'bg-blue-700/30 border-blue-600/70 text-blue-300', emoji: '🛡️' });
                        }

                        // Education
                        else if (category.includes('education') || category.includes('tuition') || category.includes('school')) {
                          badges.push({ text: 'EDUCATION', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '📚' });
                        }

                        // Fitness & Personal Care
                        else if (category.includes('gym') || category.includes('fitness') || category.includes('sport')) {
                          badges.push({ text: 'FITNESS', color: 'bg-orange-700/30 border-orange-600/70 text-orange-300', emoji: '💪' });
                        }
                        else if (category.includes('personal_care') || category.includes('salon') || category.includes('spa')) {
                          badges.push({ text: 'PERSONAL CARE', color: 'bg-pink-700/30 border-pink-600/70 text-pink-300', emoji: '💅' });
                        }

                        // Government & Taxes
                        else if (category.includes('government') || category.includes('tax') || category.includes('cra')) {
                          badges.push({ text: 'GOVERNMENT', color: 'bg-gray-700/30 border-gray-600/70 text-gray-300', emoji: '🏛️' });
                        }

                        // Charity & Donations
                        else if (category.includes('charity') || category.includes('donation')) {
                          badges.push({ text: 'CHARITY', color: 'bg-green-700/30 border-green-600/70 text-green-300', emoji: '💝' });
                        }

                        // **ATM WITHDRAWALS** - Cash pattern tracking
                        const atmTx = forensics?.cash_patterns?.atm_transactions?.find(
                          (atm: any) => atm.line_number === idx
                        );
                        if (atmTx) {
                          badges.push({
                            text: 'ATM',
                            color: 'bg-yellow-700/30 border-yellow-600/70 text-yellow-300',
                            emoji: '💰'
                          });
                        }

                        // **GAMBLING** - High risk behavioral pattern
                        if (forensics?.gambling?.transactions?.some(
                          (g: any) => g.date === txn.originalDate?.toISOString().split('T')[0] &&
                            Math.abs(parseFloat(g.amount) - txn.amount) < 0.01
                        )) {
                          badges.push({
                            text: 'GAMBLING',
                            color: 'bg-red-500/20 border-red-500/50 text-red-400',
                            emoji: '🎰'
                          });
                        }

                        return (
                        <tr
                          key={idx}
                          className={`hover:bg-gray-700/30 transition-all border-l-4 ${payCycleBg} ${bankColor} ${borderClasses}`}
                        >
                          <td className="px-2 md:px-4 py-3 text-xs md:text-sm text-gray-300 whitespace-nowrap">
                            {txn.date}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-xs md:text-sm text-white">
                            <div className="flex flex-col gap-2">
                              <span
                                className="cursor-pointer hover:text-blue-400 hover:underline"
                                onClick={() => handleCategorizeTxn(txn.description)}
                                title="Click to categorize"
                              >
                                {txn.description}
                              </span>
                              <div className="flex items-center gap-1 flex-wrap">
                                {badges.map((badge, i) => (
                                  <span key={i} className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold border ${badge.color} whitespace-nowrap`}>
                                    {badge.emoji} {badge.text}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </td>
                          <td className={`px-2 md:px-4 py-3 text-xs md:text-sm font-medium text-right whitespace-nowrap ${
                            txn.type === 'CREDIT' ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {txn.type === 'CREDIT' ? '+' : '-'}${formatMoney(txn.amount, 2)}
                          </td>
                          <td className="hidden md:table-cell px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                              txn.type === 'CREDIT'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {txn.type}
                            </span>
                          </td>
                          <td className={`px-2 md:px-4 py-3 text-xs md:text-sm text-right font-mono whitespace-nowrap ${
                            txn.balance > 5000 ? 'text-green-400 font-bold' :
                            txn.balance > 2000 ? 'text-green-500' :
                            txn.balance > 1000 ? 'text-green-600' :
                            txn.balance > 500 ? 'text-emerald-600' :
                            txn.balance > 100 ? 'text-yellow-600' :
                            txn.balance > 0 ? 'text-orange-500' :
                            txn.balance === 0 ? 'text-gray-400' :
                            txn.balance > -100 ? 'text-red-400' :
                            txn.balance > -500 ? 'text-red-500' :
                            txn.balance > -1000 ? 'text-red-600' :
                            'text-red-700 font-bold'
                          }`}>
                            ${formatMoney(txn.balance, 2)}
                          </td>
                        </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              </div> {/* End Scrollable Content Wrapper */}

              {/* Footer */}
              <div className="bg-gray-800/30 border-t border-gray-700/50 p-4">
                <div className="text-xs text-gray-400 text-center">
                  Showing {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''} from the last 365 days
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Per-Applicant Analytics Modal */}
      {applicantAnalyticsModalOpen && selectedApplicantAnalytics && (() => {
        const decision = selectedApplicantAnalytics;

        // Extract transactions from raw_ibv_data
        interface Transaction {
          date: string;
          description: string;
          amount: number;
          type: 'CREDIT' | 'DEBIT';
          balance: number;
          originalDate: Date;
          category?: string;
        }

        let transactions: Transaction[] = [];

        // Check for accounts in all possible nested locations
        const accountsData = decision.raw_ibv_data?.accounts
          || decision.raw_ibv_data?.ibv_data?.accounts
          || decision.raw_ibv_data?.raw_ibv_data?.accounts
          || decision.raw_ibv_data?.raw_ibv_data?.ibv_data?.accounts;

        if (accountsData?.[0]?.transactions) {
          const now = new Date();
          const yearAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 365);

          transactions = accountsData[0].transactions
            .map((txn: any) => {
              const txnDate = new Date(txn.date || txn.timestamp || txn.posted_date);
              const description = txn.description || txn.memo || txn.name || 'Unknown Transaction';

              // Simple category detection based on description
              let category = 'Other';
              const lowerDesc = description.toLowerCase();
              if (lowerDesc.includes('payroll') || lowerDesc.includes('salary') || lowerDesc.includes('deposit')) {
                category = 'Income';
              } else if (lowerDesc.includes('fee') || lowerDesc.includes('nsf') || lowerDesc.includes('overdraft')) {
                category = 'Fees';
              } else if (lowerDesc.includes('loan') || lowerDesc.includes('lending') || lowerDesc.includes('etransfer')) {
                category = 'Lending';
              } else if (lowerDesc.includes('grocery') || lowerDesc.includes('food')) {
                category = 'Food';
              } else if (lowerDesc.includes('rent') || lowerDesc.includes('utilities')) {
                category = 'Housing';
              } else if (lowerDesc.includes('gas') || lowerDesc.includes('fuel') || lowerDesc.includes('transit')) {
                category = 'Transportation';
              }

              return {
                date: txnDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
                description,
                amount: Math.abs(txn.amount || 0),
                type: (txn.amount || 0) >= 0 ? 'CREDIT' : 'DEBIT',
                balance: txn.balance || txn.running_balance || 0,
                originalDate: txnDate,
                category
              };
            })
            .filter((txn: Transaction) => txn.originalDate && txn.originalDate >= yearAgo)
            .sort((a: Transaction, b: Transaction) =>
              a.originalDate.getTime() - b.originalDate.getTime()
            );
        }

        // Calculate data for charts

        // 1. Balance over time (weekly samples)
        const balanceData: { date: string; balance: number }[] = [];
        if (transactions.length > 0) {
          const weekMs = 7 * 24 * 60 * 60 * 1000;
          const now = Date.now();
          const yearAgo = now - (365 * 24 * 60 * 60 * 1000);

          for (let time = yearAgo; time <= now; time += weekMs) {
            const weekDate = new Date(time);
            // Find the closest transaction balance
            const closestTxn = transactions
              .filter(t => t.originalDate.getTime() <= time)
              .sort((a, b) => Math.abs(a.originalDate.getTime() - time) - Math.abs(b.originalDate.getTime() - time))[0];

            if (closestTxn) {
              balanceData.push({
                date: weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                balance: closestTxn.balance
              });
            }
          }
        }

        // 2. Income deposits over time (monthly)
        const incomeByMonth: { month: string; income: number }[] = [];
        const monthMap = new Map<string, number>();

        transactions.filter(t => t.type === 'CREDIT' && t.category === 'Income').forEach(txn => {
          const monthKey = txn.originalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
          monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + txn.amount);
        });

        Array.from(monthMap.entries())
          .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
          .forEach(([month, income]) => {
            incomeByMonth.push({ month, income });
          });

        // 3. Fees over time (monthly cumulative)
        const feesByMonth: { month: string; fees: number; cumulative: number }[] = [];
        const feeMonthMap = new Map<string, number>();

        transactions.filter(t => t.type === 'DEBIT' && t.category === 'Fees').forEach(txn => {
          const monthKey = txn.originalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
          feeMonthMap.set(monthKey, (feeMonthMap.get(monthKey) || 0) + txn.amount);
        });

        let cumulativeFees = 0;
        Array.from(feeMonthMap.entries())
          .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
          .forEach(([month, fees]) => {
            cumulativeFees += fees;
            feesByMonth.push({ month, fees, cumulative: cumulativeFees });
          });

        // 4. Spending by category (exclude income and fees)
        const categoryMap = new Map<string, number>();
        transactions.filter(t => t.type === 'DEBIT' && t.category !== 'Fees').forEach(txn => {
          const cat = txn.category || 'Other';
          categoryMap.set(cat, (categoryMap.get(cat) || 0) + txn.amount);
        });

        const spendingByCategory = Array.from(categoryMap.entries())
          .map(([category, amount]) => ({ category, amount }))
          .sort((a, b) => b.amount - a.amount);

        // 5. Transaction volume by month
        const volumeByMonth: { month: string; count: number }[] = [];
        const volumeMonthMap = new Map<string, number>();

        transactions.forEach(txn => {
          const monthKey = txn.originalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
          volumeMonthMap.set(monthKey, (volumeMonthMap.get(monthKey) || 0) + 1);
        });

        Array.from(volumeMonthMap.entries())
          .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
          .forEach(([month, count]) => {
            volumeByMonth.push({ month, count });
          });

        // Color palette
        const COLORS = ['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];

        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={() => setApplicantAnalyticsModalOpen(false)}
          >
            <div
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-cyan-500/30 rounded-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto shadow-2xl shadow-cyan-500/20 animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 border-b border-cyan-500/30 p-6 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{decision.customer_name} - Analytics</h3>
                      <p className="text-xs text-gray-400">
                        Transaction trends over the last 365 days • {transactions.length} transactions analyzed
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setApplicantAnalyticsModalOpen(false)}
                    className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Charts Container */}
              <div className="p-6 space-y-8">
                {transactions.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    No transaction data available for this applicant
                  </div>
                ) : (
                  <>
                    {/* Balance Over Time */}
                    {balanceData.length > 0 && (
                      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-cyan-500/20 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-cyan-400" />
                          Balance Over Time
                        </h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={balanceData}>
                            <defs>
                              <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                              dataKey="date"
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                            />
                            <YAxis
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                              tickFormatter={(value) => `$${formatMoney(value)}`}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: '1px solid #06b6d4',
                                borderRadius: '8px',
                                color: '#fff'
                              }}
                              formatter={(value: any) => [`$${formatMoney(value, 2)}`, 'Balance']}
                            />
                            <Line
                              type="monotone"
                              dataKey="balance"
                              stroke="#06b6d4"
                              strokeWidth={3}
                              dot={{ fill: '#06b6d4', strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6, fill: '#06b6d4' }}
                              fill="url(#balanceGradient)"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Income Deposits Over Time */}
                    {incomeByMonth.length > 0 && (
                      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-green-500/20 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-green-400" />
                          Income Deposits by Month
                        </h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={incomeByMonth}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                              dataKey="month"
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                            />
                            <YAxis
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                              tickFormatter={(value) => `$${formatMoney(value)}`}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: '1px solid #10b981',
                                borderRadius: '8px',
                                color: '#fff'
                              }}
                              formatter={(value: any) => [`$${formatMoney(value, 2)}`, 'Income']}
                            />
                            <Bar dataKey="income" fill="#10b981" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Fees Over Time */}
                    {feesByMonth.length > 0 && (
                      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-red-500/20 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                          Bank Fees Over Time
                        </h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={feesByMonth}>
                            <defs>
                              <linearGradient id="feesGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                              dataKey="month"
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                            />
                            <YAxis
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                              tickFormatter={(value) => `$${formatMoney(value)}`}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: '1px solid #ef4444',
                                borderRadius: '8px',
                                color: '#fff'
                              }}
                              formatter={(value: any) => [`$${formatMoney(value, 2)}`, 'Fees']}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="fees"
                              stroke="#f59e0b"
                              strokeWidth={2}
                              dot={{ fill: '#f59e0b', r: 3 }}
                              name="Monthly Fees"
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulative"
                              stroke="#ef4444"
                              strokeWidth={3}
                              dot={{ fill: '#ef4444', r: 4 }}
                              name="Cumulative Fees"
                              fill="url(#feesGradient)"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Spending by Category */}
                    {spendingByCategory.length > 0 && (
                      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-purple-500/20 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <PieChartIcon className="w-5 h-5 text-purple-400" />
                          Spending by Category
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={spendingByCategory}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={(props: any) => {
                                  const entry = spendingByCategory[props.index];
                                  const percent = props.percent || 0;
                                  return `${entry.category} ${(percent * 100).toFixed(0)}%`;
                                }}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="amount"
                              >
                                {spendingByCategory.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: '#1f2937',
                                  border: '1px solid #8b5cf6',
                                  borderRadius: '8px',
                                  color: '#fff'
                                }}
                                formatter={(value: any) => `$${formatMoney(value, 2)}`}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-2">
                            {spendingByCategory.map((cat, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-gray-800/30 rounded">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-4 h-4 rounded"
                                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                  />
                                  <span className="text-sm text-gray-300">{cat.category}</span>
                                </div>
                                <span className="text-sm font-semibold text-white">${formatMoney(cat.amount, 2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Transaction Volume by Month */}
                    {volumeByMonth.length > 0 && (
                      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-blue-500/20 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-blue-400" />
                          Transaction Volume by Month
                        </h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={volumeByMonth}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                              dataKey="month"
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                            />
                            <YAxis
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: '1px solid #3b82f6',
                                borderRadius: '8px',
                                color: '#fff'
                              }}
                              formatter={(value: any) => [`${value} transactions`, 'Volume']}
                            />
                            <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* System-Wide Analytics Divider */}
                    <div className="col-span-full mt-8 mb-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Target className="w-6 h-6 text-purple-400" />
                          System-Wide Analytics
                          <span className="text-sm text-gray-400 font-normal">(All Applicants)</span>
                        </h3>
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
                      </div>
                    </div>

                    {/* Pass Rate Analytics */}
                    {criteriaPassRateData.length > 0 && (
                      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-all duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-green-500/20 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white">📊 Pass Rate Analytics</h3>
                          </div>
                          <div className="text-xs text-gray-400">
                            Avg: {(criteriaPassRateData.reduce((sum, d) => sum + d.rate, 0) / criteriaPassRateData.length).toFixed(1)}%
                          </div>
                        </div>
                        <ResponsiveContainer width="100%" height={320}>
                          <BarChart data={criteriaPassRateData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                            <defs>
                              {criteriaPassRateData.map((entry, index) => (
                                <linearGradient key={`barGradient${index}`} id={`barGradient${index}`} x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor={entry.color} stopOpacity={0.8} />
                                  <stop offset="100%" stopColor={entry.color} stopOpacity={1} />
                                </linearGradient>
                              ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                            <XAxis
                              type="number"
                              domain={[0, 100]}
                              stroke="#9ca3af"
                              tick={{ fill: '#9ca3af', fontSize: 12 }}
                              tickFormatter={(value) => `${value}%`}
                            />
                            <YAxis
                              type="category"
                              dataKey="name"
                              stroke="#9ca3af"
                              width={140}
                              tick={{ fill: '#d1d5db', fontSize: 12 }}
                            />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-lg p-3 shadow-xl">
                                      <p className="text-white font-semibold text-sm mb-1">{payload[0].payload.name}</p>
                                      <p className="text-gray-300 text-xs">
                                        Pass Rate: <span className="text-green-400 font-bold">{payload[0].value.toFixed(1)}%</span>
                                      </p>
                                      <div className="mt-2 w-full bg-gray-700 rounded-full h-1.5">
                                        <div
                                          className="bg-green-400 h-1.5 rounded-full transition-all duration-300"
                                          style={{ width: `${payload[0].value}%` }}
                                        />
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar
                              dataKey="rate"
                              radius={[0, 10, 10, 0]}
                              animationBegin={0}
                              animationDuration={1200}
                              animationEasing="ease-out"
                              maxBarSize={40}
                            >
                              {criteriaPassRateData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={`url(#barGradient${index})`}
                                  stroke={entry.color}
                                  strokeWidth={0}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* 12-Month System Charts - Grid */}
                    {(() => {
                      const now = new Date();
                      // Income Trends
                      const incomeMonthlyData = [];
                      for (let i = 11; i >= 0; i--) {
                        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
                        const monthDecisions = decisions.filter(d => {
                          const decisionDate = new Date(d.timestamp);
                          return decisionDate >= monthStart && decisionDate <= monthEnd;
                        });
                        const incomes = monthDecisions.map(d => d.income_analysis?.monthly_income || d.monthly_income_avg || 0).filter(i => i > 0);
                        const avgIncome = incomes.length > 0 ? incomes.reduce((sum, i) => sum + i, 0) / incomes.length : 0;
                        incomeMonthlyData.push({ month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), avgIncome: parseFloat(avgIncome.toFixed(2)) });
                      }

                      // Fee Trends
                      const feeMonthlyData = [];
                      for (let i = 11; i >= 0; i--) {
                        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
                        const monthDecisions = decisions.filter(d => {
                          const decisionDate = new Date(d.timestamp);
                          return decisionDate >= monthStart && decisionDate <= monthEnd;
                        });
                        const fees = monthDecisions.map(d => d.fee_analysis?.total_fees || d.total_fees || 0).filter(f => f > 0);
                        const avgFee = fees.length > 0 ? fees.reduce((sum, f) => sum + f, 0) / fees.length : 0;
                        feeMonthlyData.push({ month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), avgFee: parseFloat(avgFee.toFixed(2)) });
                      }

                      // NSF Trends
                      const nsfMonthlyData = [];
                      for (let i = 11; i >= 0; i--) {
                        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
                        const monthDecisions = decisions.filter(d => {
                          const decisionDate = new Date(d.timestamp);
                          return decisionDate >= monthStart && decisionDate <= monthEnd;
                        });
                        const nsfCounts = monthDecisions.map(d => d.fee_analysis?.nsf_count || d.nsf_count || 0);
                        const avgNSF = nsfCounts.length > 0 ? nsfCounts.reduce((sum, n) => sum + n, 0) / nsfCounts.length : 0;
                        nsfMonthlyData.push({ month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), avgNSF: parseFloat(avgNSF.toFixed(2)) });
                      }

                      // Approval Trend
                      const approvalMonthlyData = [];
                      for (let i = 11; i >= 0; i--) {
                        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
                        const monthDecisions = decisions.filter(d => {
                          const decisionDate = new Date(d.timestamp);
                          return decisionDate >= monthStart && decisionDate <= monthEnd;
                        });
                        const approved = monthDecisions.filter(d => d.approved).length;
                        const total = monthDecisions.length;
                        const rate = total > 0 ? (approved / total) * 100 : 0;
                        approvalMonthlyData.push({ month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), rate: parseFloat(rate.toFixed(1)), total });
                      }

                      return (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* 12-Month Approval Trend */}
                          <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/5 border border-purple-500/20 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-purple-400" />
                                12-Month Approval Trend
                              </h4>
                              <div className="text-xs text-gray-400">{approvalMonthlyData.reduce((sum, d) => sum + d.total, 0)} decisions</div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                              <LineChart data={approvalMonthlyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} domain={[0, 100]} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #a855f7', borderRadius: '8px', color: '#fff' }} formatter={(value: any) => [`${value}%`, 'Approval Rate']} />
                                <Line type="monotone" dataKey="rate" stroke="#a855f7" strokeWidth={3} dot={{ r: 4, fill: '#a855f7' }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>

                          {/* 12-Month Income Trends */}
                          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-green-400" />
                                12-Month Income Trends
                              </h4>
                              <div className="text-xs text-gray-400">Avg: ${formatMoney(incomeMonthlyData.reduce((sum, d) => sum + d.avgIncome, 0) / incomeMonthlyData.filter(d => d.avgIncome > 0).length || 0, 0)}</div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                              <BarChart data={incomeMonthlyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #10b981', borderRadius: '8px', color: '#fff' }} formatter={(value: any) => [`$${formatMoney(value, 0)}`, 'Avg Income']} />
                                <Bar dataKey="avgIncome" fill="#10b981" radius={[8, 8, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>

                          {/* 12-Month Fee Trends */}
                          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/5 border border-orange-500/20 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-orange-400" />
                                12-Month Fee Trends
                              </h4>
                              <div className="text-xs text-gray-400">Avg: ${formatMoney(feeMonthlyData.reduce((sum, d) => sum + d.avgFee, 0) / feeMonthlyData.filter(d => d.avgFee > 0).length || 0, 0)}</div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                              <LineChart data={feeMonthlyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #f97316', borderRadius: '8px', color: '#fff' }} formatter={(value: any) => [`$${formatMoney(value, 2)}`, 'Avg Fees']} />
                                <Line type="monotone" dataKey="avgFee" stroke="#f97316" strokeWidth={3} dot={{ r: 4, fill: '#f97316' }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>

                          {/* 12-Month NSF Trends */}
                          <div className="bg-gradient-to-br from-red-500/10 to-pink-500/5 border border-red-500/20 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-red-400" />
                                12-Month NSF Trends
                              </h4>
                              <div className="text-xs text-gray-400">Avg: {(nsfMonthlyData.reduce((sum, d) => sum + d.avgNSF, 0) / nsfMonthlyData.filter(d => d.avgNSF > 0).length || 0).toFixed(1)} per application</div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                              <BarChart data={nsfMonthlyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #ef4444', borderRadius: '8px', color: '#fff' }} formatter={(value: any) => [value.toFixed(2), 'Avg NSF']} />
                                <Bar dataKey="avgNSF" fill="#ef4444" radius={[8, 8, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-800/30 border-t border-gray-700/50 p-4">
                <button
                  onClick={() => setApplicantAnalyticsModalOpen(false)}
                  className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Analysis History Modal */}
      {analysisHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setAnalysisHistoryModalOpen(false)}>
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-cyan-500/30 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-b border-cyan-500/30 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <svg className="w-7 h-7 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Analysis History
                  </h2>
                  <p className="text-sm text-gray-400 mt-1">All deep analysis and forensic reports • {analysisHistory.length} total</p>
                </div>
                <button onClick={() => setAnalysisHistoryModalOpen(false)} className="w-10 h-10 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors">✕</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {analysisHistory.length === 0 ? (
                <div className="text-center py-12"><svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg><p className="text-gray-400">No analysis history yet</p></div>
              ) : (
                <div className="space-y-3">
                  {analysisHistory.map((job) => (
                    <div
                      key={job.id}
                      className={`border-2 rounded-lg p-4 transition-all cursor-pointer hover:scale-[1.02] relative overflow-hidden
                        ${job.analysis_type === 'forensic' ? 'bg-orange-500/5 border-orange-500/30 hover:border-orange-500/50' : 'bg-purple-500/5 border-purple-500/30 hover:border-purple-500/50'}`}
                      onClick={() => job.status === 'complete' && setSelectedHistoryJob(job)}
                      style={job.status === 'running' ? {
                        animation: job.analysis_type === 'forensic'
                          ? 'shine-orange 3s ease-in-out infinite'
                          : 'shine-purple 3s ease-in-out infinite'
                      } : {}}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${job.analysis_type === 'forensic' ? 'bg-orange-500/20 text-orange-400' : 'bg-purple-500/20 text-purple-400'}`}>{job.analysis_type === 'forensic' ? '🔍 FORENSIC' : '🔬 DEEP ANALYSIS'}</span>
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${job.status === 'running' ? 'bg-blue-500/20 text-blue-400' : job.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>{job.status.toUpperCase()}</span>
                          </div>
                          <p className="text-sm text-gray-300 mb-1">Job ID: <span className="font-mono text-cyan-400">{job.job_id}</span></p>
                          <div className="flex items-center gap-4 text-xs text-gray-400">
                            <span>📅 {new Date(job.created_at).toLocaleString()}</span>
                            {job.transaction_count && (
                              <span>
                                {job.status === 'running' ? (
                                  <span className="text-blue-400 font-semibold animate-pulse">
                                    ⚙️ Processing {job.transaction_count} lines...
                                  </span>
                                ) : (
                                  <span>📊 {job.transaction_count} lines</span>
                                )}
                              </span>
                            )}
                            {job.model_used && <span>🤖 {job.model_used}</span>}
                            {job.status === 'running' ? (
                              <span className="text-blue-400 font-semibold">
                                ⏱️ {Math.floor((Date.now() - new Date(job.created_at).getTime()) / 1000)}s elapsed
                              </span>
                            ) : job.processing_time_ms ? (
                              <span>⏱️ {(job.processing_time_ms / 1000).toFixed(1)}s</span>
                            ) : null}
                          </div>
                        </div>
                        {job.status === 'complete' && <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected Job Detail */}
      {selectedHistoryJob && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setSelectedHistoryJob(null)}>
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-purple-500/30 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-500/20 to-orange-500/20 border-b border-purple-500/30 p-4">
              <div className="flex items-center justify-between">
                <div><h3 className="text-xl font-bold text-white">{selectedHistoryJob.analysis_type === 'forensic' ? '🔍 Forensic Analysis' : '🔬 Deep Analysis'}</h3><p className="text-xs text-gray-400 mt-1">{new Date(selectedHistoryJob.created_at).toLocaleString()} • {selectedHistoryJob.model_used}</p></div>
                <button onClick={() => setSelectedHistoryJob(null)} className="w-8 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors">✕</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6"><div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6"><pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">{selectedHistoryJob.analysis_result}</pre></div></div>
          </div>
        </div>
      )}
    </div>
  );
}
